/**
 * ProductConfigWizard - Visual "Self-Service" Order Configuration
 * 
 * Three distinct modes with no overlap:
 * 1. Quick Order - Bundle selection driven by backend (quick_order_bundles)
 * 2. Visual Builder - Flow-driven configuration from backend (FlowDrivenBuilder)
 * 3. Expert Setup - Full control, dense form, no visuals
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Alert, AlertDescription } from './ui/alert';
import { 
    Disc, 
    Zap,
    Palette,
    Settings2, 
    ChevronRight, 
    ChevronLeft,
    Check,
    Clock,
    AlertCircle,
    Loader2,
    ArrowRight,
    Sparkles,
    Eye,
    Package,
    Crown,
    MessageSquare
} from 'lucide-react';
import FlowDrivenBuilder from './FlowDrivenBuilder';
import ExpertSetupBuilder from './ExpertSetupBuilder';
import { useCurrency, formatCurrencyAmount, convertFromEur } from '../hooks/useCurrency';
import { useTranslation } from '../contexts/TranslationContext';
import CatalogueNumberInput from './CatalogueNumberInput';
import BundleCategoryPlaceholder from './BundleCategoryPlaceholder';

import { API_BASE } from '../lib/apiBase';
const API_URL = API_BASE;
const getToken = () => localStorage.getItem('token');

// Legacy BUDGET_SPECS kept for backward compatibility with Expert Mode
const BUDGET_SPECS = {
    '7': {
        low: {
            color_mode: 'black', vinyl_weight: '140g', speed: '45', test_pressings: '0',
            sleeve_package: 'none', inner_sleeve: 'none', outer_protection: 'none', printing: 'none', spine: '3mm',
            production_weeks: 5
        },
        standard: {
            color_mode: 'black', vinyl_weight: '140g', speed: '45', test_pressings: '5',
            sleeve_package: 'standard', inner_sleeve: 'paper', outer_protection: 'none', printing: 'none', spine: '3mm',
            production_weeks: 6
        },
        premium: {
            color_mode: 'color', vinyl_weight: '140g', speed: '45', test_pressings: '5',
            sleeve_package: 'printed', inner_sleeve: 'printed', outer_protection: 'pvc', printing: '4/0', spine: '3mm',
            production_weeks: 8
        }
    },
    '10': {
        low: {
            color_mode: 'black', vinyl_weight: '140g', speed: '33', test_pressings: '0',
            sleeve_package: 'none', inner_sleeve: 'none', outer_protection: 'none', printing: 'none', spine: '3mm',
            production_weeks: 6
        },
        standard: {
            color_mode: 'black', vinyl_weight: '140g', speed: '33', test_pressings: '5',
            sleeve_package: 'standard', inner_sleeve: 'paper', outer_protection: 'none', printing: 'none', spine: '3mm',
            production_weeks: 7
        },
        premium: {
            color_mode: 'black', vinyl_weight: '180g', speed: '33', test_pressings: '10',
            sleeve_package: 'gatefold', inner_sleeve: 'printed', outer_protection: 'pvc', printing: '4/0', spine: '5mm',
            production_weeks: 9
        }
    },
    '12': {
        low: {
            color_mode: 'black', vinyl_weight: '140g', speed: '33', test_pressings: '0',
            sleeve_package: 'none', inner_sleeve: 'none', outer_protection: 'none', printing: 'none', spine: '3mm',
            production_weeks: 6
        },
        standard: {
            color_mode: 'black', vinyl_weight: '140g', speed: '33', test_pressings: '5',
            sleeve_package: 'standard', inner_sleeve: 'generic', outer_protection: 'none', printing: 'none', spine: '3mm',
            production_weeks: 8
        },
        premium: {
            color_mode: 'black', vinyl_weight: '180g', speed: '33', test_pressings: '10',
            sleeve_package: 'gatefold', inner_sleeve: 'printed', outer_protection: 'pvc', printing: '4/0', spine: '5mm',
            production_weeks: 10
        }
    }
};

// Visual Builder step configuration
const VISUAL_STEPS = [
    { id: 'record', title: 'The Record', subtitle: 'Weight & color of your vinyl' },
    { id: 'inner', title: 'Inner Protection', subtitle: 'What goes around the record' },
    { id: 'cover', title: 'Main Cover', subtitle: 'Your outer sleeve design' },
    { id: 'extras', title: 'Extras', subtitle: 'Additional options' },
    { id: 'review', title: 'Review', subtitle: 'Confirm your selection' },
];

/**
 * Main Product Configuration Wizard Component
 */
export default function ProductConfigWizard({ 
    mode = null,
    initialSpecs = null, 
    onConfigChange,
    onComplete,
    onModeChange,
    pricingMessage = null,
    isImmersiveMode = false,
    orderMetadata = null,
    orderTitle = '',
    titleInputRef = null,
    productFamily = 'vinyl',
    savedFlowConfig = null, // { flowId, flowVersion, selections }
    // Public landing pages pass an ISO 4217 override that reflects the
    // marketing site's currency handoff. Anonymous visitors then see
    // every price quote in that currency for the whole session.
    currencyOverride = null,
    // Opt-in variant forwarded to FlowDrivenBuilder — see there for
    // the contract. Default null preserves V1 behaviour exactly.
    recordStepVariant = null,
    // Landing page "Show Prices" toggle. When false, prices are hidden
    // throughout the anonymous visual/expert flow. Calc/navigation
    // untouched. Defaults true (authenticated/admin + existing pages).
    showPrices = true,
}) {
    const { t } = useTranslation();
    // Use mode prop directly if provided (controlled mode), otherwise use internal state
    const [internalMode, setInternalMode] = useState(null);
    const wizardMode = mode !== undefined && onModeChange ? mode : internalMode;
    
    // Currency handling for consistent pricing display
    const { 
        customerCurrency, 
        rates, 
        loading: currencyLoading,
        toCustomerCurrency,
        formatInCustomerCurrency
    } = useCurrency(localStorage.getItem('token'), { override: currencyOverride });    
    const setWizardMode = (newMode) => {
        console.log('[ProductConfigWizard] Setting mode to:', newMode);
        if (onModeChange) {
            onModeChange(newMode);
        } else {
            setInternalMode(newMode);
        }
    };
    
    // Auto-select visual mode if there's a saved flow config
    useEffect(() => {
        if (savedFlowConfig?.flowId && savedFlowConfig?.selections && wizardMode === null) {
            console.log('[ProductConfigWizard] Restoring saved flow config, auto-selecting visual mode');
            setWizardMode('visual');
        }
    }, [savedFlowConfig]);

    // Auto-select Quick Order mode when editing an existing bundle order so
    // "Edit" from the final review page returns straight to the combined
    // configure step (not the mode picker). Visual-builder orders handled above.
    useEffect(() => {
        if (initialSpecs?._bundle_id && !savedFlowConfig?.flowId && wizardMode === null) {
            setWizardMode('quick');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialSpecs]);
    
    const [configurableOptions, setConfigurableOptions] = useState([]);
    const [visualAssets, setVisualAssets] = useState([]);
    const [loading, setLoading] = useState(true);

    // Effective vinyl-order-modes config for this customer's org — powers
    // the mode-selection screen's disabled cards + default pre-selection.
    // Falls open on "all enabled + default=quick" if the fetch fails, so
    // network hiccups never lock a customer out of ordering.
    const [orderModesConfig, setOrderModesConfig] = useState({
        modes: { quick: { enabled: true }, visual: { enabled: true }, expert: { enabled: true } },
        default_mode: 'quick',
    });
    useEffect(() => {
        // Only vinyl uses the 3-mode picker today. Other product
        // families short-circuit to their single flow, so skip the fetch.
        if (productFamily !== 'vinyl') return;
        const token = localStorage.getItem('token');
        if (!token) return;
        const API_URL = process.env.REACT_APP_BACKEND_URL;
        fetch(`${API_URL}/api/admin/customer/vinyl-order-modes`, {
            headers: { Authorization: `Bearer ${token}` },
        })
            .then((r) => (r.ok ? r.json() : null))
            .then((cfg) => { if (cfg) setOrderModesConfig(cfg); })
            .catch(() => { /* keep the fall-open defaults */ });
    }, [productFamily]);
    const [error, setError] = useState(null);
    
    // Configuration state
    const [specs, setSpecs] = useState(initialSpecs || {
        format: '12',
        quantity: 500,
        color_mode: 'black',
        vinyl_weight: '140g',
        speed: '33',
        test_pressings: '0',
        sleeve_package: 'standard',
        inner_sleeve: 'generic',
        outer_protection: 'none',
        printing: 'none',
        spine: '3mm',
        center_label: '4color',
        runout_engraving: 'none',
        engraving_text: '',
    });
    
    // Price estimate state
    const [priceEstimate, setPriceEstimate] = useState(null);
    const [isCalculating, setIsCalculating] = useState(false);

    // Preloaded Guided-Setup flow config — we eagerly fetch this as soon as
    // the wizard mounts so that by the time the user clicks "Build your
    // <product>" we can hand the data straight to FlowDrivenBuilder without
    // it having to refetch. Eliminates the full-page loader between mode
    // selection and the configurator.
    const [preloadedFlowConfig, setPreloadedFlowConfig] = useState(null);
    const [flowPreloading, setFlowPreloading] = useState(false);
    // Tracks a mode the user clicked while preload is still in-flight; when
    // the data arrives we automatically continue to that mode.
    const [pendingMode, setPendingMode] = useState(null);
    const pendingModeRef = useRef(null);
    pendingModeRef.current = pendingMode;

    // Prefetch Guided-Setup flow config in parallel with the main options
    // fetch above. Failure is silent — FlowDrivenBuilder will retry as a
    // fallback if our preload didn't make it in time.
    useEffect(() => {
        let cancelled = false;
        const prefetch = async () => {
            // Skip if we're loading a saved flow with a specific ID — that's
            // handled by FlowDrivenBuilder itself and isn't worth caching.
            if (savedFlowConfig?.flowId) return;
            setFlowPreloading(true);
            try {
                const res = await fetch(
                    `${API_URL}/api/product-config/flows/by-mode/visual?product_family=${encodeURIComponent(productFamily)}`
                );
                if (!cancelled && res.ok) {
                    const flow = await res.json();
                    setPreloadedFlowConfig(flow);
                    // If the user already clicked Guided Setup while we
                    // were loading, continue automatically now.
                    if (pendingModeRef.current === 'visual') {
                        setWizardMode('visual');
                        setPendingMode(null);
                    }
                }
            } catch (_err) {
                // ignored — FlowDrivenBuilder will refetch
            } finally {
                if (!cancelled) setFlowPreloading(false);
            }
        };
        prefetch();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [productFamily, savedFlowConfig?.flowId]);

    // Wrap setWizardMode so Guided Setup waits for the prefetch when needed
    const handleModeSelectFromMenu = useCallback((mode) => {
        if (mode === 'visual' && !preloadedFlowConfig && flowPreloading) {
            // Defer transition until prefetch resolves
            setPendingMode('visual');
            return;
        }
        setWizardMode(mode);
        setPendingMode(null);
    // setWizardMode is a stable closure from the hosting component scope
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [preloadedFlowConfig, flowPreloading]);

    // Load options and assets — assets re-fetch when `language` flips
    // so translated Visual Builder labels appear without a page reload.
    const { language: activeLang } = useTranslation();
    useEffect(() => {
        const loadData = async () => {
            try {
                setLoading(true);
                const langQ = activeLang && activeLang !== 'en' ? `&lang=${encodeURIComponent(activeLang)}` : '';
                const [optionsRes, assetsRes] = await Promise.all([
                    fetch(`${API_URL}/api/pricing/configurable-options/customer`),
                    fetch(`${API_URL}/api/visual-builder/assets?product_family=${encodeURIComponent(productFamily)}${langQ}`)
                ]);
                
                if (!optionsRes.ok) throw new Error('Failed to load product options');
                
                const options = await optionsRes.json();
                setConfigurableOptions(options);
                
                if (assetsRes.ok) {
                    const assets = await assetsRes.json();
                    setVisualAssets(assets);
                }
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };
        loadData();
    }, [activeLang, productFamily]);

    // Get price estimate
    const getEstimate = useCallback(async (config) => {
        setIsCalculating(true);
        try {
            const payload = {
                format: config.format,
                quantity: parseInt(config.quantity) || 100,
                color: config.color_mode === 'color' ? 'Colored' : 'Black',
                vinyl_weight_g: parseInt(config.vinyl_weight?.replace('g', '')) || 140,
                sleeve_type: config.sleeve_package,
                print_colors: config.printing,
                test_pressings: parseInt(config.test_pressings) || 0
            };
            
            const authToken = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
            const res = await fetch(`${API_URL}/api/pricing/customer/estimate`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    // Best-effort auth. Anonymous callers see the list
                    // price; logged-in customers get their org's
                    // global_discount_percent applied — matching the
                    // quote / invoice / order-review paths across the
                    // rest of the platform.
                    ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
                },
                body: JSON.stringify(payload)
            });
            
            if (!res.ok) throw new Error('Failed to get price estimate');
            const result = await res.json();
            setPriceEstimate(result);
            return result;
        } catch (err) {
            console.error('Price estimate error:', err);
            return null;
        } finally {
            setIsCalculating(false);
        }
    }, []);

    // Notify parent of config changes
    useEffect(() => {
        if (onConfigChange) {
            onConfigChange(specs, priceEstimate);
        }
    }, [specs, priceEstimate, onConfigChange]);

    // Get asset by category and option
    const getAsset = (category, optionId) => {
        return visualAssets.find(a => a.category === category && a.option_id === optionId);
    };

    // Get assets by category
    const getAssetsByCategory = (category) => {
        return visualAssets.filter(a => a.category === category).sort((a, b) => a.display_order - b.display_order);
    };

    // Check if an option value is available based on conditions
    const isValueAvailable = (value, option) => {
        if (!value.conditions) return true;
        for (const [condKey, condValues] of Object.entries(value.conditions)) {
            if (!condValues.includes(specs[condKey])) {
                return false;
            }
        }
        return true;
    };

    // Get options by category
    const vinylOptions = configurableOptions.filter(o => o.category === 'vinyl');
    const sleeveOptions = configurableOptions.filter(o => o.category === 'sleeve');

    // No more full-page skeleton when options are still loading. The Mode
    // Selection screen renders immediately; individual cards show inline
    // spinners only when the user actually clicks something while data is
    // still in flight (see `pendingMode` below).
    if (error) {
        return (
            <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
            </Alert>
        );
    }

    // ==================== MODE SELECTION ====================
    if (wizardMode === null) {
        return (
            <ModeSelection 
                onSelect={handleModeSelectFromMenu}
                getAssetsByCategory={getAssetsByCategory}
                pricingMessage={pricingMessage}
                orderTitle={orderTitle}
                onTitleChange={orderMetadata?.onTitleChange}
                titleInputRef={titleInputRef}
                productFamily={productFamily}
                pendingMode={pendingMode}
                orderModesConfig={orderModesConfig}
            />
        );
    }

    // ==================== QUICK ORDER ====================
    if (wizardMode === 'quick') {
        return (
            <QuickOrderFlow
                specs={specs}
                setSpecs={setSpecs}
                getAssetsByCategory={getAssetsByCategory}
                getEstimate={getEstimate}
                priceEstimate={priceEstimate}
                isCalculating={isCalculating}
                onBack={() => setWizardMode(null)}
                onComplete={onComplete}
                customerCurrency={customerCurrency}
                rates={rates}
                productFamily={productFamily}
                currencyLoading={currencyLoading}
                orderMetadata={orderMetadata}
            />
        );
    }
    // ==================== VISUAL BUILDER ====================
    // Now using flow-driven builder that reads configuration from backend
    if (wizardMode === 'visual') {
        return (
            <FlowDrivenBuilder
                specs={specs}
                setSpecs={setSpecs}
                getEstimate={getEstimate}
                priceEstimate={priceEstimate}
                isCalculating={isCalculating}
                onBack={() => setWizardMode(null)}
                onComplete={onComplete}
                isImmersiveMode={isImmersiveMode}
                orderMetadata={orderMetadata}
                productFamily={productFamily}
                savedFlowConfig={savedFlowConfig}
                initialFlowConfig={preloadedFlowConfig}
                currencyOverride={currencyOverride}
                recordStepVariant={recordStepVariant}
                showPrices={showPrices}
            />
        );
    }

    // ==================== EXPERT MODE ====================
    // Now using ExpertSetupBuilder - a form-based flow-driven builder
    return (
        <ExpertSetupBuilder
            flowMode="expert"
            initialSelections={savedFlowConfig?._flow_selections}
            quantity={specs.quantity || 500}
            productFamily={productFamily}
            onComplete={(config, estimate) => {
                // Only call onComplete when user confirms
                if (onComplete) {
                    onComplete({ ...specs, ...config }, estimate);
                }
            }}
            onBack={() => setWizardMode(null)}
            showPrices={showPrices}
        />
    );
}

// ==================== MODE SELECTION ====================
function ModeSelection({ onSelect, getAssetsByCategory, pricingMessage, orderTitle, onTitleChange, titleInputRef, productFamily = 'vinyl', pendingMode = null, orderModesConfig = null }) {
    const { t } = useTranslation();
    const [showTitleError, setShowTitleError] = useState(false);

    // Resolve per-mode enablement + default from the admin-managed
    // config. Falls open to "all enabled + default=quick" when no config
    // was supplied (non-vinyl families skip the fetch).
    const cfg = orderModesConfig || {
        modes: { quick: { enabled: true }, visual: { enabled: true }, expert: { enabled: true } },
        default_mode: 'quick',
    };
    const isQuickEnabled = cfg.modes?.quick?.enabled !== false;
    const isVisualEnabled = cfg.modes?.visual?.enabled !== false;
    const isExpertEnabled = cfg.modes?.expert?.enabled !== false;
    const defaultMode = cfg.default_mode || 'quick';

    const handleModeSelect = (mode) => {
        // Block clicks on admin-disabled cards even if the DOM click
        // sneaks through (the card ALWAYS renders per the spec —
        // hidden-click prevention lives here, not on `pointer-events`).
        const enabledMap = { quick: isQuickEnabled, visual: isVisualEnabled, expert: isExpertEnabled };
        if (!enabledMap[mode]) return;
        if (!orderTitle || orderTitle.trim() === '') {
            setShowTitleError(true);
            // Focus the title input
            if (titleInputRef?.current) {
                titleInputRef.current.focus();
                titleInputRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
            return;
        }
        setShowTitleError(false);
        onSelect(mode);
    };
    
    const isTitleEmpty = !orderTitle || orderTitle.trim() === '';

    // Phase 3 fix: derive a human label from productFamily so all customer
    // wording follows the active family ("Vinyl", "CD", "DVD", "Blu-ray").
    const familyLabelMap = { vinyl: 'Vinyl', cd: 'CD', dvd: 'DVD', bluray: 'Blu-ray' };
    const familyLabel = familyLabelMap[productFamily] || 'Vinyl';
    const familyLabelLower = familyLabel.toLowerCase();
    
    return (
        <Card className="w-full border-0 shadow-none bg-transparent">
            <CardHeader className="text-center pb-4 px-0">
                <CardTitle className="text-2xl">{t('wizard.configure_your', 'Configure Your {label}', { label: familyLabel })}</CardTitle>
                {pricingMessage ? (
                    <p className="text-base text-slate-600 mt-1">{pricingMessage}</p>
                ) : (
                    <CardDescription className="text-base">
                        Choose how you'd like to set up your order
                    </CardDescription>
                )}
            </CardHeader>
            <CardContent className="px-0">
                {/* Album / Release Title — the only detail captured on the
                    initial screen. Artist, catalogue number and PO number
                    are collected later on the Review Your Order page. A
                    title is required before any setup mode can start; we
                    use subtle inline validation rather than a large banner. */}
                <div className="max-w-md mx-auto mb-8">
                    <Input
                        ref={titleInputRef}
                        value={orderTitle}
                        onChange={(e) => {
                            onTitleChange?.(e.target.value);
                            if (showTitleError && e.target.value.trim()) setShowTitleError(false);
                        }}
                        placeholder={t('order.album_title_placeholder', 'Album / release title')}
                        className={`h-11 text-center text-base ${showTitleError ? 'border-amber-400 focus-visible:ring-amber-300' : ''}`}
                        data-testid="order-title-input"
                        aria-invalid={showTitleError}
                    />
                    {showTitleError ? (
                        <p className="mt-2 text-xs text-amber-600 flex items-center justify-center gap-1.5" data-testid="order-title-error">
                            <AlertCircle className="h-3.5 w-3.5 flex-shrink-0" />
                            {t('wizard.enter_title_before_configuring', 'Enter a title to continue')}
                        </p>
                    ) : (
                        <p className="mt-2 text-xs text-slate-400 text-center">
                            {t('order.title_required_hint_short', 'Required to start your order')}
                        </p>
                    )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto py-4">
                    {/* Quick Order */}
                    <button
                        onClick={() => handleModeSelect('quick')}
                        disabled={!isQuickEnabled}
                        className={`group p-6 rounded-2xl border-2 transition-all text-left relative ${
                            !isQuickEnabled
                                ? 'border-border bg-slate-50/50 opacity-40 cursor-not-allowed'
                                : isTitleEmpty
                                    ? 'border-border bg-slate-50/50 opacity-60 cursor-not-allowed'
                                    : 'border-border hover:border-amber-500 hover:bg-amber-50/50'
                        }`}
                        data-testid="mode-quick"
                        aria-disabled={!isQuickEnabled}
                    >
                        {!isQuickEnabled && (
                            <span className="absolute top-3 right-3 inline-block rounded-full bg-slate-200 px-2 py-0.5 text-[10px] font-medium text-slate-600 uppercase tracking-wide" data-testid="mode-quick-unavailable">
                                Unavailable
                            </span>
                        )}
                        {isQuickEnabled && defaultMode === 'quick' && (
                            <span className="absolute top-3 right-3 inline-block rounded-full bg-amber-100 border border-amber-200 px-2 py-0.5 text-[10px] font-medium text-amber-800 uppercase tracking-wide" data-testid="mode-quick-default">
                                Default
                            </span>
                        )}
                        <div
                            className={`h-14 w-14 rounded-xl flex items-center justify-center mb-4 transition-all ${
                                isTitleEmpty ? 'bg-slate-100' : 'shadow-sm group-hover:shadow'
                            }`}
                            style={!isTitleEmpty ? { backgroundImage: 'linear-gradient(135deg, hsl(35 92% 45%), hsl(42 96% 55%))' } : undefined}
                        >
                            <Zap className={`h-7 w-7 ${isTitleEmpty ? 'text-slate-400' : 'text-white'}`} />
                        </div>
                        <h3 className={`text-lg font-semibold mb-1 ${isTitleEmpty ? 'text-slate-500' : 'text-slate-900'}`}>
                            Quick Order
                        </h3>
                        <p className="text-sm text-slate-500 mb-3">
                            3 simple choices. We'll handle the rest.
                        </p>
                        <Badge variant="secondary" className={`text-xs ${isTitleEmpty ? 'bg-slate-100 text-slate-500' : 'bg-amber-100 text-amber-800 border border-amber-200'}`}>
                            Fastest • Recommended
                        </Badge>
                        <div className={`flex items-center text-sm font-medium mt-4 ${isTitleEmpty ? 'text-slate-400' : 'text-amber-700'}`}>
                            Get started <ArrowRight className={`h-4 w-4 ml-1 ${!isTitleEmpty && 'group-hover:translate-x-1'} transition-transform`} />
                        </div>
                    </button>

                    {/* Guided Setup */}
                    <button
                        onClick={() => handleModeSelect('visual')}
                        disabled={!isVisualEnabled || pendingMode === 'visual'}
                        className={`group p-6 rounded-2xl border-2 transition-all text-left relative ${
                            !isVisualEnabled
                                ? 'border-border bg-slate-50/50 opacity-40 cursor-not-allowed'
                                : isTitleEmpty
                                    ? 'border-border bg-slate-50/50 opacity-60 cursor-not-allowed'
                                    : pendingMode === 'visual'
                                        ? 'border-purple-400 bg-purple-50/50 cursor-wait'
                                        : 'border-border hover:border-purple-400 hover:bg-purple-50/50'
                        }`}
                        data-testid="mode-visual"
                        aria-disabled={!isVisualEnabled}
                    >
                        {!isVisualEnabled && (
                            <span className="absolute top-3 right-3 inline-block rounded-full bg-slate-200 px-2 py-0.5 text-[10px] font-medium text-slate-600 uppercase tracking-wide" data-testid="mode-visual-unavailable">
                                Unavailable
                            </span>
                        )}
                        {isVisualEnabled && defaultMode === 'visual' && (
                            <span className="absolute top-3 right-3 inline-block rounded-full bg-purple-100 border border-purple-200 px-2 py-0.5 text-[10px] font-medium text-purple-700 uppercase tracking-wide" data-testid="mode-visual-default">
                                Default
                            </span>
                        )}
                        <div className={`h-14 w-14 rounded-xl flex items-center justify-center mb-4 transition-colors ${
                            isTitleEmpty ? 'bg-slate-100' : 'bg-purple-100 group-hover:bg-purple-200'
                        }`}>
                            <Palette className={`h-7 w-7 ${isTitleEmpty ? 'text-slate-400' : 'text-purple-600'}`} />
                        </div>
                        <h3 className={`text-lg font-semibold mb-1 ${isTitleEmpty ? 'text-slate-500' : 'text-slate-900'}`}>
                            Guided Setup
                        </h3>
                        <p className="text-sm text-slate-500 mb-3">
                            See exactly what you're ordering, step by step.
                        </p>
                        <Badge variant="secondary" className={`text-xs ${isTitleEmpty ? 'bg-slate-100 text-slate-500' : 'bg-purple-100 text-purple-700'}`}>
                            Most visual • Interactive
                        </Badge>
                        <div className={`flex items-center text-sm font-medium mt-4 ${isTitleEmpty ? 'text-slate-400' : 'text-purple-600'}`}>
                            {pendingMode === 'visual' ? (
                                <>
                                    <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                                    Preparing builder…
                                </>
                            ) : (
                                <>
                                    Build your {familyLabelLower} <ArrowRight className={`h-4 w-4 ml-1 ${!isTitleEmpty && 'group-hover:translate-x-1'} transition-transform`} />
                                </>
                            )}
                        </div>
                    </button>

                    {/* Expert Setup */}
                    <button
                        onClick={() => handleModeSelect('expert')}
                        disabled={!isExpertEnabled}
                        className={`group p-6 rounded-2xl border-2 transition-all text-left relative ${
                            !isExpertEnabled
                                ? 'border-border bg-slate-50/50 opacity-40 cursor-not-allowed'
                                : isTitleEmpty
                                    ? 'border-border bg-slate-50/50 opacity-60 cursor-not-allowed'
                                    : 'border-border hover:border-slate-400 hover:bg-background'
                        }`}
                        data-testid="mode-expert"
                        aria-disabled={!isExpertEnabled}
                    >
                        {!isExpertEnabled && (
                            <span className="absolute top-3 right-3 inline-block rounded-full bg-slate-200 px-2 py-0.5 text-[10px] font-medium text-slate-600 uppercase tracking-wide" data-testid="mode-expert-unavailable">
                                Unavailable
                            </span>
                        )}
                        {isExpertEnabled && defaultMode === 'expert' && (
                            <span className="absolute top-3 right-3 inline-block rounded-full bg-slate-200 border border-slate-300 px-2 py-0.5 text-[10px] font-medium text-slate-700 uppercase tracking-wide" data-testid="mode-expert-default">
                                Default
                            </span>
                        )}
                        <div className={`h-14 w-14 rounded-xl flex items-center justify-center mb-4 transition-colors ${
                            isTitleEmpty ? 'bg-slate-100' : 'bg-slate-100 group-hover:bg-slate-200'
                        }`}>
                            <Settings2 className={`h-7 w-7 ${isTitleEmpty ? 'text-slate-400' : 'text-slate-600'}`} />
                        </div>
                        <h3 className={`text-lg font-semibold mb-1 ${isTitleEmpty ? 'text-slate-500' : 'text-slate-900'}`}>
                            Expert Setup
                        </h3>
                        <p className="text-sm text-slate-500 mb-3">
                            Full control over every option.
                        </p>
                        <Badge variant="secondary" className={`text-xs ${isTitleEmpty ? 'bg-slate-100 text-slate-500' : ''}`}>
                            For professionals
                        </Badge>
                        <div className={`flex items-center text-sm font-medium mt-4 ${isTitleEmpty ? 'text-slate-400' : 'text-slate-600'}`}>
                            Configure manually <ArrowRight className={`h-4 w-4 ml-1 ${!isTitleEmpty && 'group-hover:translate-x-1'} transition-transform`} />
                        </div>
                    </button>
                </div>
            </CardContent>
        </Card>
    );
}

// ==================== QUICK ORDER FLOW ====================
// ==================== QUICK ORDER FLOW - BUNDLE DRIVEN ====================
function QuickOrderFlow({
    specs,
    setSpecs,
    getAssetsByCategory,
    getEstimate,
    priceEstimate,
    isCalculating,
    onBack,
    onComplete,
    // Currency props
    customerCurrency = 'EUR',
    rates = null,
    currencyLoading = false,
    productFamily = 'vinyl',
    orderMetadata = null
}) {
    const { t, language } = useTranslation();
    // Resolve a bundle image to a renderable src: absolute/data URLs are used
    // as-is, relative `/api/...` paths are prefixed with the API base.
    const resolveBundleImage = (u) => {
        if (!u) return null;
        return /^https?:\/\//.test(u) || u.startsWith('data:') ? u : `${API_URL}${u}`;
    };
    // Family-aware wording (Phase 3 fix)
    const familyLabelMap = { vinyl: 'Vinyl', cd: 'CD', dvd: 'DVD', bluray: 'Blu-ray' };
    const familyLabel = familyLabelMap[productFamily] || 'Vinyl';
    const familyLabelLower = familyLabel.toLowerCase();
    const isVinylFamily = (productFamily || 'vinyl').toLowerCase() === 'vinyl';
    const [step, setStep] = useState(0); // 0: select bundle, 1: configure (quantity + pricing + details)
    const [bundles, setBundles] = useState([]);
    const [selectedBundle, setSelectedBundle] = useState(null);
    const [loading, setLoading] = useState(true);
    const [bundlePrices, setBundlePrices] = useState({});
    // Monotonic request id — guards against a slower, older /quote response
    // overwriting the price for the quantity the customer just picked.
    const priceReqRef = useRef(0);
    
    // Helper function to convert EUR to customer currency
    const convertPrice = (eurAmount) => {
        if (!rates || customerCurrency === 'EUR' || !eurAmount) return eurAmount;
        return convertFromEur(eurAmount, customerCurrency, rates);
    };
    
    // Format price in customer's currency
    const formatPrice = (eurAmount, decimals = 0) => {
        if (eurAmount === null || eurAmount === undefined) return '—';
        const converted = convertPrice(eurAmount);
        return formatCurrencyAmount(converted, customerCurrency, { decimals });
    };

    // Fetch bundles on mount and whenever productFamily or language changes.
    useEffect(() => {
        const fetchBundles = async () => {
            try {
                const langQ = language && language !== 'en' ? `&lang=${encodeURIComponent(language)}` : '';
                const res = await fetch(
                    `${API_URL}/api/product-config/bundles?product_family=${encodeURIComponent(productFamily)}${langQ}`,
                    { headers: { 'Authorization': `Bearer ${getToken()}` } }
                );
                if (res.ok) {
                    const data = await res.json();
                    setBundles(data);
                }
            } catch (error) {
                console.error('Failed to load bundles:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchBundles();
    }, [productFamily, language]);

    // Fetch prices for all bundles when they load
    useEffect(() => {
    // No need to pre-fetch prices - we'll fetch when bundle is selected
    }, [bundles]);

    // Shared price fetch used by bundle-select, quantity-change and edit-restore.
    const fetchPriceFor = async (bundle, qty) => {
        const reqId = ++priceReqRef.current;
        try {
            const res = await fetch(`${API_URL}/api/sales/quote`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${getToken()}` },
                body: JSON.stringify({
                    quantity: qty,
                    line_items: [
                        { sales_sku: bundle.base_sales_sku, quantity_multiplier: 1 },
                        ...(bundle.default_addon_skus || []).map(sku => ({ sales_sku: sku, quantity_multiplier: 1 }))
                    ]
                })
            });
            if (res.ok) {
                const data = await res.json();
                // Ignore stale responses — a newer quantity was requested after this.
                if (reqId !== priceReqRef.current) return;
                // Quote-gate short-circuit — null out numeric estimates so
                // nothing renders a bogus zero.
                setBundlePrices(prev => ({
                    ...prev,
                    [bundle.id]: data.requires_quote ? {
                        total: null,
                        unit: null,
                        quantity: qty,
                        requires_quote: true,
                        quote_gated_skus: data.quote_gated_skus || [],
                        requires_quote_source: data.requires_quote_source || null,
                    } : {
                        total: data.total_estimate || 0,
                        unit: data.unit_price_estimate || 0,
                        quantity: qty,
                        requires_quote: false,
                    }
                }));
            }
        } catch (e) {
            console.error('Price fetch error:', e);
        }
    };

    // When bundle is selected, calculate final price with selected quantity
    const handleBundleSelect = async (bundle) => {
        setSelectedBundle(bundle);
        const defaultQty = bundle.default_quantity || 500;

        // Explicit bundled_options dict wins over everything — admins set it
        // in QuickBundlesManager → BundledSpecsSection. When unset for a
        // given key, fall back to the legacy SKU/title heuristic so old
        // bundles continue to work without a migration.
        const bundled = bundle.bundled_options || {};

        // 1. vinyl_weight resolution
        //    a) explicit bundled_options.vinyl_weight_g (integer 140/180)
        //    b) explicit bundled_options.vinyl_weight   (string "140g"/"180g")
        //    c) legacy: text-match "180" in SKU/title/format_label
        //    d) preserve current spec
        let derivedWeight = specs.vinyl_weight;
        if (typeof bundled.vinyl_weight_g === 'number') {
            derivedWeight = `${bundled.vinyl_weight_g}g`;
        } else if (typeof bundled.vinyl_weight === 'string' && bundled.vinyl_weight) {
            derivedWeight = bundled.vinyl_weight;
        } else {
            const skuAndTitle = `${bundle.base_sales_sku || ''} ${bundle.title || ''} ${bundle.format_label || ''}`.toLowerCase();
            if (skuAndTitle.includes('180')) {
                derivedWeight = '180g';
            }
        }

        // Apply bundle options to specs. The spread of `bundled` last lets
        // the admin override ANY key (format, color_mode, speed, etc.) by
        // adding it to bundled_options — same precedence rule as legacy
        // `bundle.bundled_options` callers expected.
        //
        // For non-vinyl families (CD/DVD/Blu-ray) we DON'T inherit the
        // vinyl defaults (`specs` on this wizard is always initialised to
        // vinyl values — see line ~161). Otherwise a CD Quick Order would
        // silently save `speed=33`, `vinyl_weight=140g`, `test_pressings=0`
        // etc. into vinyl_specs and downstream summary cards would
        // display them as if the customer had chosen them. Starting from
        // a blank `{}` for non-vinyl families keeps `vinyl_specs` clean
        // and the family-aware summary in OrderFilesPage renders only
        // the fields the bundle actually pre-filled.
        const family = (productFamily || 'vinyl').toLowerCase();
        const baseSpecs = family === 'vinyl' ? specs : {};
        const newSpecs = {
            ...baseSpecs,
            format: bundled.format || (family === 'vinyl' ? specs.format : undefined),
            quantity: defaultQty,
            ...(family === 'vinyl' ? { vinyl_weight: derivedWeight } : {}),
            ...bundled
        };
        setSpecs(newSpecs);
        setStep(1);
        fetchPriceFor(bundle, defaultQty);
    };

    // When quantity changes, recalculate price
    const handleQuantitySelect = (qty) => {
        setSpecs(prev => ({ ...prev, quantity: qty }));
        if (selectedBundle) {
            fetchPriceFor(selectedBundle, qty);
        }
    };

    // Get tier badge styling
    const getTierBadge = (tier) => {        switch (tier) {
            case 'budget':
                return { label: 'Budget', className: 'bg-slate-100 text-slate-600' };
            case 'standard':
                return { label: 'Standard', className: 'bg-amber-100 text-amber-800 border border-amber-200' };
            case 'premium':
                return { label: 'Premium', className: 'bg-violet-100 text-violet-700 border border-violet-200' };
            default:
                return { label: tier || 'Bundle', className: 'bg-slate-100 text-slate-600' };
        }
    };

    // Hide the tier badge when every displayed bundle shares the same tier
    // (a "Standard"/"Budget"/... chip on every card is redundant noise).
    const allSameTier = bundles.length > 0
        && new Set(bundles.map((b) => b.tier || '')).size <= 1;

    // Humanise bundled_options keys/values for the always-visible
    // "What's included" list (e.g. vinyl_weight_g -> Weight / 180g, format 7 -> 7").
    const prettyIncludedKey = (key) => {
        const map = { vinyl_weight_g: 'Weight', vinyl_weight: 'Weight', format: 'Format', color_mode: 'Colour', speed: 'Speed', test_pressings: 'Test pressings', sleeve_package: 'Sleeve' };
        return map[key] || key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
    };
    const prettyIncludedValue = (key, value) => {
        if (value === null || value === undefined || value === '') return '—';
        if (key === 'vinyl_weight_g') return `${value}g`;
        if (key === 'format') return `${String(value).replace(/"/g, '')}"`;
        if (key === 'color_mode') return value === 'color' ? 'Coloured' : value === 'black' ? 'Black' : String(value);
        if (key === 'speed') return `${value} RPM`;
        return String(value);
    };

    // Edit-restore: returning from the final review page to edit an existing
    // Quick Order jumps straight to the combined configure step with the
    // previously-selected bundle and quantity intact.
    useEffect(() => {
        if (!selectedBundle && specs?._bundle_id && bundles.length > 0) {
            const existing = bundles.find(b => b.id === specs._bundle_id);
            if (existing) {
                const qty = parseInt(specs.quantity) || existing.default_quantity || 500;
                setSelectedBundle(existing);
                setStep(1);
                fetchPriceFor(existing, qty);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [bundles]);

    // Loading state
    if (loading) {
        return (
            <Card className="w-full">
                <CardContent className="py-12 flex flex-col items-center justify-center">
                    <Loader2 className="h-8 w-8 animate-spin text-emerald-500 mb-4" />
                    <p className="text-slate-500">{t('wizard.loading_packages', 'Loading packages...')}</p>
                </CardContent>
            </Card>
        );
    }

    // Step 0: Bundle Selection
    if (step === 0) {
        return (
            <Card className="w-full">
                <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                        <div>
                            <Badge
                                className="mb-2 text-white border-0 shadow-sm hover:brightness-105"
                                style={{ backgroundImage: 'linear-gradient(135deg, hsl(35 92% 45%), hsl(42 96% 55%))' }}
                            >
                                Quick Order
                            </Badge>
                            <CardTitle>{t('quick_order.choose_package', 'Choose Your Package')}</CardTitle>
                            <CardDescription>{t('wizard.select_prebuilt_package', 'Select a pre-configured {family} package', { family: familyLabelLower })}</CardDescription>
                        </div>
                        <Button variant="ghost" size="sm" onClick={onBack}>
                            Change mode
                        </Button>
                    </div>
                </CardHeader>
                <CardContent>
                    {bundles.length === 0 ? (
                        <Alert>
                            <AlertCircle className="h-4 w-4" />
                            <AlertDescription>
                                No packages available. Please contact support or use Guided Setup mode.
                            </AlertDescription>
                        </Alert>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 py-4">
                            {bundles.map((bundle) => {
                                const tierBadge = getTierBadge(bundle.tier);
                                const isPremium = bundle.tier === 'premium';
                                const rec = bundle.recommendation_badge;
                                const showTierBadge = !allSameTier;
                                const img = resolveBundleImage(bundle.image_url);
                                const formatText = bundle.format_label
                                    || (bundle.bundled_options?.format
                                        ? `${bundle.bundled_options.format}" ${familyLabelLower}`
                                        : null);

                                return (
                                    <button
                                        key={bundle.id}
                                        onClick={() => handleBundleSelect(bundle)}
                                        className="group relative flex h-full flex-col text-left rounded-2xl border-2 border-border bg-white overflow-hidden transition-all duration-200 hover:border-amber-400 hover:shadow-lg hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400"
                                        data-testid={`quick-bundle-${bundle.id}`}
                                    >
                                        {/* Image / neutral category graphic + recommendation badge overlay */}
                                        <div className="relative w-full aspect-[16/9] bg-slate-100 overflow-hidden">
                                            {img ? (
                                                <img
                                                    src={img}
                                                    alt={bundle.title}
                                                    loading="lazy"
                                                    className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                                                />
                                            ) : (
                                                <BundleCategoryPlaceholder family={productFamily} />
                                            )}
                                            {rec === 'last_used' && (
                                                <Badge
                                                    className="absolute top-3 left-3 bg-emerald-600 text-white border-0 shadow-sm"
                                                    data-testid={`bundle-badge-last-used-${bundle.id}`}
                                                >
                                                    Last Used
                                                </Badge>
                                            )}
                                            {rec === 'most_used' && (
                                                <Badge
                                                    className="absolute top-3 left-3 bg-white/95 text-sky-800 border border-sky-200 shadow-sm"
                                                    data-testid={`bundle-badge-most-used-${bundle.id}`}
                                                >
                                                    Most Used
                                                </Badge>
                                            )}
                                        </div>

                                        {/* Body */}
                                        <div className="flex flex-1 flex-col p-5">
                                            {showTierBadge && (
                                                <Badge className={`mb-2 w-fit ${tierBadge.className}`}>
                                                    {isPremium && <Crown className="h-3 w-3 mr-1" />}
                                                    {tierBadge.label}
                                                </Badge>
                                            )}
                                            <h3 className="text-lg font-semibold text-slate-900 mb-1">
                                                {bundle.title}
                                            </h3>
                                            {bundle.subtitle && (
                                                <p className="text-sm text-slate-500 line-clamp-2">
                                                    {bundle.subtitle}
                                                </p>
                                            )}
                                            <div className="mt-auto flex items-center justify-between pt-4">
                                                {formatText ? (
                                                    <span className="flex items-center gap-1.5 text-xs text-slate-500">
                                                        <Disc className="h-3 w-3" />
                                                        {formatText}
                                                    </span>
                                                ) : <span />}
                                                <span className="flex items-center gap-1 text-sm font-medium text-amber-700 transition-transform group-hover:translate-x-0.5">
                                                    {t('wizard.select_to_see_pricing', 'Select')}
                                                    <ChevronRight className="h-4 w-4" />
                                                </span>
                                            </div>
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </CardContent>
            </Card>
        );
    }

    // Step 1: Configure Your Package (quantity + pricing + package details)
    if (step === 1 && selectedBundle) {
        const presets = selectedBundle.allowed_quantity_choices || [100, 250, 500, 1000];
        const currentQty = parseInt(specs.quantity) || selectedBundle.default_quantity || 500;
        const price = bundlePrices[selectedBundle.id];
        const tierBadge = getTierBadge(selectedBundle.tier);
        const isQuoteGated = !!(price && price.requires_quote);
        // Only trust a price that matches the quantity currently selected —
        // prevents a stale (previous-quantity) price from showing or being
        // submitted while a new quote is still in flight.
        const showPrice = !!(price && price.quantity === currentQty);
        const priceUnavailable = showPrice && !price.requires_quote && !(price.total > 0);
        const canContinue = showPrice && (price.requires_quote || price.total > 0);
        const bundledEntries = Object.entries(selectedBundle.bundled_options || {});

        return (
            <Card className="w-full">
                <CardHeader className="pt-4 pb-2">
                    <CardTitle>{t('quick_order.configure_package', 'Configure Your Package')}</CardTitle>
                    <CardDescription>{t('quick_order.configure_desc', "Choose your quantity and confirm what's included")}</CardDescription>
                </CardHeader>
                <CardContent>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start pt-1 pb-2">
                        {/* LEFT: package summary, description, inclusions, timeline */}
                        <div className="bg-white rounded-2xl p-5 border border-border space-y-4" data-testid="config-package-summary">
                            <div>
                                {!allSameTier && (
                                    <Badge className={`mb-2 ${tierBadge.className}`}>
                                        {tierBadge.label}
                                    </Badge>
                                )}
                                <h3 className="text-xl font-bold text-slate-900" data-testid="config-package-title">
                                    {selectedBundle.title}
                                </h3>
                                {selectedBundle.format_label && (
                                    <p className="text-sm text-slate-500 mt-1">{selectedBundle.format_label}</p>
                                )}
                            </div>

                            {/* What's included — always shown */}
                            <div className="border-t border-slate-100 pt-3" data-testid="config-package-included">
                                <p className="text-[10px] uppercase tracking-wider text-slate-400 font-medium mb-2">
                                    {t('quick_order.whats_included', "What's included")}
                                </p>
                                {selectedBundle.description && (
                                    <p className="text-sm text-slate-600 leading-relaxed mb-2 whitespace-pre-line">{selectedBundle.description.trim()}</p>
                                )}
                                {bundledEntries.length > 0 ? (
                                    <div className="space-y-1">
                                        {bundledEntries.map(([key, value]) => (
                                            <div key={key} className="flex justify-between text-xs">
                                                <span className="text-slate-500">{prettyIncludedKey(key)}</span>
                                                <span className="text-slate-700 font-medium">{prettyIncludedValue(key, value)}</span>
                                            </div>
                                        ))}
                                    </div>
                                ) : !selectedBundle.description ? (
                                    <p className="text-xs text-slate-500">
                                        {selectedBundle.format_label || t('quick_order.standard_inclusions', 'Standard production inclusions for this package.')}
                                    </p>
                                ) : null}
                            </div>

                            {/* Estimated timeline */}
                            <div className="bg-slate-50 rounded-lg px-4 py-3 border border-slate-100">
                                <p className="text-[10px] uppercase tracking-wider text-slate-400 font-medium mb-1.5">{t('flow.estimated_timeline', 'Estimated timeline')}</p>
                                {isVinylFamily && (
                                    <div className="flex justify-between text-xs">
                                        <span className="text-slate-500">{t('quick_order.test_press', 'Test press')}</span>
                                        <span className="text-slate-700 font-medium">~4 weeks</span>
                                    </div>
                                )}
                                <div className={`flex justify-between text-xs ${isVinylFamily ? 'mt-1' : ''}`}>
                                    <span className="text-slate-500">{t('quick_order.production', 'Production')}</span>
                                    <span className="text-slate-700 font-medium">{isVinylFamily ? '~8 weeks' : '~2 weeks'}</span>
                                </div>
                                <p className="text-[10px] text-slate-400 italic mt-1.5">{t('wizard.exact_dates_confirmed', 'Exact dates confirmed after submission.')}</p>
                            </div>
                        </div>

                        {/* RIGHT: quantity options + live price */}
                        <div className="bg-white rounded-2xl p-5 border border-border space-y-4" data-testid="config-quantity-panel">
                            <div>
                                <h4 className="text-sm font-semibold text-slate-800 mb-0.5">{t('quick_order.how_many_copies', 'How many copies?')}</h4>
                                <p className="text-xs text-slate-500 mb-3">{t('quick_order.select_quantity', 'Select your production quantity')}</p>
                                <div className="flex flex-wrap gap-2.5">
                                    {presets.map(qty => {
                                        const isSelected = currentQty === qty;
                                        return (
                                            <button
                                                key={qty}
                                                onClick={() => handleQuantitySelect(qty)}
                                                className={`flex-shrink-0 min-w-[78px] px-4 py-3 rounded-xl border-2 bg-white transition-all ${
                                                    isSelected
                                                        ? 'shadow-sm'
                                                        : 'hover:border-amber-400 hover:bg-amber-50/40'
                                                }`}
                                                style={isSelected ? {
                                                    borderColor: 'hsl(38 92% 50%)',
                                                    backgroundColor: 'hsl(45 100% 96%)',
                                                    boxShadow: '0 0 0 3px hsl(42 96% 75% / 0.45)',
                                                } : undefined}
                                                data-testid={`quick-qty-${qty}`}
                                            >
                                                <span className="block text-lg font-bold text-slate-900">{qty}</span>
                                                <span className="block text-[11px] text-slate-500">copies</span>
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* Live price — updates immediately on quantity change.
                                Shows a spinner until the quote for the SELECTED
                                quantity arrives, so a stale price is never shown. */}
                            {(currencyLoading || !showPrice) ? (
                                <div className="text-center py-4 bg-slate-50 rounded-lg border border-slate-100" data-testid="config-quick-price-loading">
                                    <Loader2 className="h-5 w-5 animate-spin text-slate-400 mx-auto" />
                                </div>
                            ) : (
                                <div className="bg-slate-50 rounded-lg px-4 py-4 border border-slate-100" data-testid="config-quick-price-panel">
                                    {price.requires_quote ? (
                                        <>
                                            <p className="text-lg font-semibold text-amber-800" data-testid="config-quick-price-on-request">
                                                Price on Request
                                            </p>
                                            <p className="text-[11px] text-amber-700 mt-1">
                                                This configuration needs a custom quote
                                            </p>
                                        </>
                                    ) : priceUnavailable ? (
                                        <p className="text-sm font-medium text-slate-600" data-testid="config-quick-price-unavailable">
                                            {t('quick_order.price_unavailable', 'Price unavailable for this quantity — please contact support.')}
                                        </p>
                                    ) : (
                                        <>
                                            <p className="text-3xl font-bold text-slate-900 font-display" data-testid="config-quick-unit-price">
                                                {formatPrice(price.unit, 2)} <span className="text-sm font-medium text-slate-500">/ unit</span>
                                            </p>
                                            <p className="text-sm text-slate-500 mt-1" data-testid="config-quick-total">
                                                {formatPrice(price.total, 0)} total
                                            </p>
                                        </>
                                    )}
                                </div>
                            )}

                            {/* Note to production team — reuses the order-level
                                `notes` field; captured here and persisted to the
                                order on Continue (same field admin/production see). */}
                            {orderMetadata?.onNoteChange && (
                                <div data-testid="config-production-note">
                                    <label className="block text-sm font-semibold text-slate-800 mb-1">
                                        {t('quick_order.production_note_label', 'Note to production team')} <span className="text-slate-300 font-normal">{t('common.optional_parenthesis', '(optional)')}</span>
                                    </label>
                                    <Textarea
                                        value={orderMetadata.note || ''}
                                        onChange={(e) => orderMetadata.onNoteChange(e.target.value)}
                                        rows={3}
                                        maxLength={2000}
                                        placeholder={t('quick_order.production_note_ph', 'Deadline, special requests, anything our team should know…')}
                                        className="resize-none text-sm"
                                        data-testid="config-production-note-input"
                                    />
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Bottom navigation — Change package + single Continue */}
                    <div className="flex flex-col-reverse sm:flex-row sm:justify-between gap-3 pt-4 border-t">
                        <Button
                            variant="outline"
                            onClick={() => { setSelectedBundle(null); setStep(0); }}
                            className="w-full sm:w-auto"
                            data-testid="quick-change-package"
                        >
                            <ChevronLeft className="h-4 w-4 mr-1" /> Change package
                        </Button>
                        <Button
                            onClick={() => {
                                // Pass bundle info along with specs + the quote-gate
                                // hint so OrderProductPage routes the submit correctly.
                                // Order details (title/artist/catalogue/PO) and the
                                // production note are captured on the final review page.
                                const finalSpecs = {
                                    ...specs,
                                    _bundle_id: selectedBundle.id,
                                    _bundle_title: selectedBundle.title,
                                    _bundle_skus: [selectedBundle.base_sales_sku, ...(selectedBundle.default_addon_skus || [])],
                                    _requires_quote: isQuoteGated,
                                    _quote_gated_skus: isQuoteGated ? (price?.quote_gated_skus || null) : null,
                                };
                                onComplete && onComplete(finalSpecs, isQuoteGated ? {
                                    total_estimate: null,
                                    unit_price_estimate: null,
                                    requires_quote: true,
                                } : (price ? {
                                    total_estimate: price.total,
                                    unit_price_estimate: price.unit
                                } : null));
                            }}
                            disabled={!canContinue}
                            className="w-full sm:w-auto text-white border-0 shadow-md hover:shadow-lg hover:brightness-105 transition-all"
                            style={{ backgroundImage: 'linear-gradient(135deg, hsl(35 92% 45%), hsl(42 96% 55%))' }}
                            data-testid="quick-confirm"
                        >
                            <Check className="h-4 w-4 mr-2" />
                            {isQuoteGated ? 'Request Quote' : t('quick_order.continue', 'Continue')}
                        </Button>
                    </div>
                </CardContent>
            </Card>
        );
    }

    // Fallback
    return null;
}

// ==================== VISUAL BUILDER FLOW ====================
function VisualBuilderFlow({
    specs,
    setSpecs,
    getAssetsByCategory,
    getEstimate,
    priceEstimate,
    isCalculating,
    onBack,
    onComplete,
    isImmersiveMode = false,
    orderMetadata = null,
    // Currency formatter piped in from ProductConfigWizard so the
    // immersive price ribbon + "Your Order" summary honour the anonymous
    // Landing Page currency handoff (?currency=USD/GBP/DKK/…).
    formatInCustomerCurrency = (v, d = 2) => `€${(v ?? 0).toFixed(d)}`,
}) {
    const { t } = useTranslation();
    const [step, setStep] = useState(0); // 0: format+qty, 1: record, 2: inner, 3: cover, 4: extras, 5: review

    // Load price when specs change
    useEffect(() => {
        const timer = setTimeout(() => {
            getEstimate(specs);
        }, 500);
        return () => clearTimeout(timer);
    }, [specs, getEstimate]);

    // Step titles
    const steps = [
        { title: 'Format & Quantity', subtitle: 'Start with the basics' },
        { title: 'The Record', subtitle: 'Weight & color of your vinyl' },
        { title: 'Inner Protection', subtitle: 'What protects your record' },
        { title: 'Main Cover', subtitle: 'Your outer sleeve design' },
        { title: 'Extras', subtitle: 'Additional options' },
        { title: 'Review', subtitle: 'Confirm your selection' },
    ];

    // Price display component - Immersive version with integrated CTA
    const PriceBar = ({ variant = 'default', onNext, onBack, canContinue = true, isLastStep = false, currentStep = 0 }) => {
        // Normalize format display (handle both '12' and '12"')
        const displayFormat = specs.format?.replace(/"/g, '');
        
        if (variant === 'immersive') {
            return (
                <div className="fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur-sm border-t border-border z-40">
                    <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
                        {/* Left: Back button (hidden on step 0) */}
                        <div className="w-24">
                            {currentStep > 0 && onBack && (
                                <Button 
                                    variant="ghost" 
                                    size="sm" 
                                    onClick={onBack}
                                    className="text-slate-600 hover:text-slate-900"
                                >
                                    <ChevronLeft className="h-4 w-4 mr-1" />
                                    Back
                                </Button>
                            )}
                        </div>
                        
                        {/* Center: Config summary + price */}
                        <div className="flex items-center gap-6">
                            <div className="text-center hidden sm:block">
                                <span className="text-xs text-slate-500 block">{t('wizard.your_configuration', 'Your configuration')}</span>
                                <div className="text-sm font-medium text-slate-900 leading-snug">
                                    <span>{displayFormat}" {getWeightLabel(specs.vinyl_weight)}</span>
                                    <span className="text-slate-300 mx-1">|</span>
                                    <span>{getColorLabel(specs.color_mode)}</span>
                                    <span className="text-slate-300 mx-1">|</span>
                                    <span>{specs.quantity} units</span>
                                </div>
                            </div>
                            <div className="text-center sm:hidden">
                                <span className="text-sm font-medium text-slate-900">
                                    {displayFormat}" {getColorLabel(specs.color_mode)} · {specs.quantity}
                                </span>
                            </div>
                            {priceEstimate ? (
                                <div className="text-right">
                                    <span className="text-2xl font-bold text-slate-900 font-display">
                                        {formatInCustomerCurrency(priceEstimate.unit_price_estimate, 2)}
                                        <span className="text-sm font-medium text-slate-500 ml-1">/ unit</span>
                                    </span>
                                    <span className="text-xs text-slate-500 block mt-0.5">
                                        {formatInCustomerCurrency(priceEstimate.total_estimate, 0)} total
                                    </span>
                                </div>
                            ) : isCalculating ? (
                                <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
                            ) : null}
                        </div>
                        
                        {/* Right: Continue/Complete CTA */}
                        <div className="w-32 flex justify-end">
                            {onNext && (
                                <Button 
                                    onClick={onNext}
                                    disabled={!canContinue}
                                    className={`${isLastStep ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-purple-600 hover:bg-purple-700'} disabled:opacity-50 disabled:cursor-not-allowed`}
                                    data-testid="immersive-continue-btn"
                                >
                                    {isLastStep ? (
                                        <>
                                            <Check className="h-4 w-4 mr-1" />
                                            Complete
                                        </>
                                    ) : (
                                        <>
                                            Continue
                                            <ChevronRight className="h-4 w-4 ml-1" />
                                        </>
                                    )}
                                </Button>
                            )}
                        </div>
                    </div>
                </div>
            );
        }
        
        return (
            <div className="flex items-center justify-between bg-background rounded-lg px-4 py-2 mb-4">
                <span className="text-sm text-slate-500">{t('flow.estimated_price', 'Estimated price')}</span>
                {priceEstimate ? (
                    <span className="font-bold text-lg text-emerald-600 font-display">
                        {formatInCustomerCurrency(priceEstimate.unit_price_estimate, 2)}
                        <span className="text-sm font-medium text-emerald-700 ml-1">/ unit</span>
                        <span className="text-xs text-slate-400 font-normal ml-2">
                            ({formatInCustomerCurrency(priceEstimate.total_estimate, 0)} total)
                        </span>
                    </span>
                ) : isCalculating ? (
                    <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
                ) : (
                    <span className="text-sm text-slate-400">—</span>
                )}
            </div>
        );
    };

    // Progress bar - Immersive version
    const ProgressBar = ({ variant = 'default' }) => {
        if (variant === 'immersive') {
            return (
                <div className="flex items-center justify-center gap-2 py-4">
                    {steps.map((s, idx) => (
                        <button
                            key={idx}
                            onClick={() => idx < step && setStep(idx)}
                            disabled={idx > step}
                            className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                                idx === step
                                    ? 'bg-purple-600 text-white'
                                    : idx < step
                                    ? 'bg-purple-100 text-purple-700 hover:bg-purple-200 cursor-pointer'
                                    : 'bg-slate-100 text-slate-400'
                            }`}
                        >
                            {idx < step && <Check className="w-3 h-3" />}
                            <span className="hidden sm:inline">{s.title}</span>
                            <span className="sm:hidden">{idx + 1}</span>
                        </button>
                    ))}
                </div>
            );
        }
        
        return (
            <div className="flex items-center gap-1.5 mb-4">
                {steps.map((_, idx) => (
                    <div
                        key={idx}
                        className={`h-1.5 flex-1 rounded-full transition-colors ${
                            idx <= step ? 'bg-purple-500' : 'bg-slate-200'
                        }`}
                    />
                ))}
            </div>
        );
    };

    // Visual option card
    const VisualOption = ({ asset, isSelected, onClick, size = 'normal' }) => (
        <button
            onClick={onClick}
            className={`relative overflow-hidden rounded-xl border-2 transition-all text-left ${
                isSelected
                    ? 'border-purple-500 ring-2 ring-purple-200'
                    : 'border-border hover:border-purple-300'
            } ${size === 'large' ? 'p-4' : 'p-3'}`}
        >
            {asset?.image_url && (
                <div className={`${size === 'large' ? 'h-28' : 'h-20'} rounded-lg overflow-hidden mb-3 bg-slate-100 animate-pulse relative`}>
                    <img 
                        src={asset.image_url} 
                        alt={asset.label}
                        loading="lazy"
                        decoding="async"
                        onLoad={(e) => {
                            e.currentTarget.classList.remove('opacity-0');
                            e.currentTarget.parentElement?.classList.remove('animate-pulse');
                        }}
                        className="w-full h-full object-cover opacity-0 transition-opacity duration-200"
                    />
                </div>
            )}
            <h4 className={`font-semibold text-slate-900 ${size === 'large' ? 'text-base' : 'text-sm'}`}>
                {asset?.label || 'Unknown'}
            </h4>
            {asset?.short_description && (
                <p className="text-xs text-slate-500 mt-0.5">{asset.short_description}</p>
            )}
            {isSelected && (
                <div className="absolute top-2 right-2 h-5 w-5 rounded-full bg-purple-500 flex items-center justify-center">
                    <Check className="h-3 w-3 text-white" />
                </div>
            )}
        </button>
    );

    // Immersive container wrapper with integrated navigation
    const ImmersiveContainer = ({ children, showPrice = true, onNext, onBack, canContinue = true, isLastStep = false, currentStep = 0 }) => (
        <div className="min-h-[calc(100vh-56px)] pb-24">
            <ProgressBar variant="immersive" />
            <div className="max-w-7xl mx-auto px-4 sm:px-6">
                <div className="grid grid-cols-1 xl:grid-cols-12 gap-6">
                    <div className="xl:col-span-8">
                        {children}
                    </div>
                    <div className="hidden xl:block xl:col-span-4">
                        <OrderSummaryPanel currentStep={currentStep} />
                    </div>
                </div>
            </div>
            {showPrice && (
                <PriceBar 
                    variant="immersive" 
                    onNext={onNext} 
                    onBack={onBack} 
                    canContinue={canContinue} 
                    isLastStep={isLastStep}
                    currentStep={currentStep}
                />
            )}
        </div>
    );

    // ==================== UNIFIED VISUAL OPTION CARD ====================
    // Reusable card component for all Visual Builder option selections
    const VisualOptionCard = ({ 
        imageUrl, 
        label, 
        description, 
        isSelected, 
        onClick, 
        size = 'default', // 'small', 'default', 'large'
        layout = 'vertical', // 'vertical', 'horizontal'
        className = ''
    }) => {
        const sizeClasses = {
            small: 'p-3',
            default: 'p-4 sm:p-5',
            large: 'p-4 sm:p-6'
        };
        
        const imageSizeClasses = {
            small: 'h-14 sm:h-16',
            default: 'h-20 sm:h-24',
            large: 'h-24 sm:h-28'
        };
        
        return (
            <button
                onClick={onClick}
                className={`
                    relative rounded-2xl border-2 transition-all text-center
                    ${sizeClasses[size]}
                    ${isSelected
                        ? 'border-purple-500 bg-purple-50 shadow-premium-lg shadow-purple-500/20'
                        : 'border-border hover:border-purple-300 hover:shadow-premium-md hover:bg-background/50'
                    }
                    ${className}
                `}
            >
                {imageUrl && (
                    <div className={`${imageSizeClasses[size]} rounded-xl overflow-hidden mb-3 bg-slate-100 animate-pulse mx-auto relative`}>
                        <img 
                            src={imageUrl} 
                            alt={label}
                            loading="lazy"
                            decoding="async"
                            onLoad={(e) => {
                                e.currentTarget.classList.remove('opacity-0');
                                e.currentTarget.parentElement?.classList.remove('animate-pulse');
                            }}
                            className="w-full h-full object-cover opacity-0 transition-opacity duration-200"
                        />
                    </div>
                )}
                <h4 className={`font-bold ${isSelected ? 'text-purple-700' : 'text-slate-900'} ${size === 'small' ? 'text-sm' : 'text-base'}`}>
                    {label}
                </h4>
                {description && (
                    <p className={`text-slate-500 mt-1 ${size === 'small' ? 'text-[10px]' : 'text-xs'}`}>
                        {description}
                    </p>
                )}
                {isSelected && (
                    <div className="absolute -top-2 -right-2 h-6 w-6 rounded-full bg-purple-500 flex items-center justify-center shadow-premium-md">
                        <Check className="h-4 w-4 text-white" />
                    </div>
                )}
            </button>
        );
    };

    // ==================== QUANTITY PRESET CARD ====================
    const QuantityCard = ({ qty, isSelected, onClick }) => (
        <button
            onClick={onClick}
            className={`
                relative px-6 py-4 rounded-2xl border-2 transition-all
                ${isSelected
                    ? 'border-purple-500 bg-purple-50 shadow-premium-lg shadow-purple-500/20'
                    : 'border-border hover:border-purple-300 hover:shadow-premium-md hover:bg-background/50'
                }
            `}
        >
            <span className={`text-xl font-bold ${isSelected ? 'text-purple-700' : 'text-slate-900'}`}>{qty}</span>
            <span className={`text-xs block ${isSelected ? 'text-purple-600' : 'text-slate-500'}`}>copies</span>
            {isSelected && (
                <div className="absolute -top-2 -right-2 h-5 w-5 rounded-full bg-purple-500 flex items-center justify-center shadow-premium-md">
                    <Check className="h-3 w-3 text-white" />
                </div>
            )}
        </button>
    );

    // ==================== TOGGLE OPTION CARD ====================
    // For simple text-only options (test pressings, outer protection)
    const ToggleOptionCard = ({ label, isSelected, onClick, size = 'default' }) => {
        const sizeClasses = {
            small: 'px-4 py-3',
            default: 'px-6 py-4',
            large: 'px-8 py-5'
        };
        
        return (
            <button
                onClick={onClick}
                className={`
                    relative rounded-2xl border-2 transition-all
                    ${sizeClasses[size]}
                    ${isSelected
                        ? 'border-purple-500 bg-purple-50 shadow-premium-lg shadow-purple-500/20'
                        : 'border-border hover:border-purple-300 hover:shadow-premium-md hover:bg-background/50'
                    }
                `}
            >
                <span className={`font-bold ${isSelected ? 'text-purple-700' : 'text-slate-900'}`}>{label}</span>
                {isSelected && (
                    <div className="absolute -top-2 -right-2 h-5 w-5 rounded-full bg-purple-500 flex items-center justify-center shadow-premium-md">
                        <Check className="h-3 w-3 text-white" />
                    </div>
                )}
            </button>
        );
    };

    // ==================== LABEL HELPERS ====================
    const getFormatLabel = (f) => ({ '7': '7" Single/EP', '10': '10" EP', '12': '12" LP' }[f?.replace(/"/g, '')] || f);
    const getWeightLabel = (w) => ({ '140g': 'Standard (140g)', '180g': 'Heavyweight (180g)' }[w] || w);
    const getColorLabel = (c) => ({ 'black': 'Black', 'color': 'Colored', 'splatter': 'Splatter/Marble', 'picture': 'Picture Disc' }[c] || c);
    const getLabelLabel = (l) => ({ '4color': '4-Colour Print', 'black': 'Black Print', 'unprinted': 'Unprinted', 'none': 'No Label' }[l] || l);
    const getInnerLabel = (i) => ({ 'none': 'None', 'paper': 'Plain Paper', 'generic': 'Generic', 'poly_lined': 'Poly-Lined', 'printed': 'Printed Inner' }[i] || i);
    const getCoverLabel = (s) => ({ 'none': 'No Sleeve', 'standard': 'Single Sleeve', 'gatefold': 'Gatefold', 'printed': 'Tip-On Deluxe' }[s] || s);
    const getEngravingLabel = (s) => s.runout_engraving === 'engraving' ? (s.engraving_text || 'Yes') : 'None';

    // ==================== ORDER SUMMARY PANEL (STICKY SIDEBAR) ====================
    const OrderSummaryPanel = ({ currentStep }) => {
        const summaryLines = [
            { label: 'Record', value: getFormatLabel(specs.format), step: 0, done: currentStep > 0 },
            { label: 'Weight', value: getWeightLabel(specs.vinyl_weight), step: 1, done: currentStep > 1 },
            { label: 'Vinyl color', value: getColorLabel(specs.color_mode), step: 1, done: currentStep > 1 },
            { label: 'Center label', value: getLabelLabel(specs.center_label), step: 1, done: currentStep > 1 },
            { label: 'Inner sleeve', value: getInnerLabel(specs.inner_sleeve), step: 2, done: currentStep > 2 },
            { label: 'Jacket', value: getCoverLabel(specs.sleeve_package), step: 3, done: currentStep > 3 },
            { label: 'Test pressings', value: parseInt(specs.test_pressings) ? `${specs.test_pressings} copies` : 'None', step: 4, done: currentStep > 4 },
            { label: 'Outer sleeve', value: specs.outer_protection === 'pvc' ? 'Poly sleeve' : 'None', step: 4, done: currentStep > 4 },
            { label: 'Engraving', value: getEngravingLabel(specs), step: 1, done: currentStep > 1 },
        ];

        return (
            <div className="sticky top-6" data-testid="order-summary-panel">
                <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
                    {/* Header */}
                    <div className="px-4 py-3 bg-gradient-to-r from-purple-50 to-slate-50 border-b border-slate-100">
                        <h3 className="text-sm font-semibold text-slate-800">{t('flow.your_order', 'Your Order')}</h3>
                        <p className="text-xs text-slate-500 mt-0.5">{specs.quantity} copies</p>
                    </div>

                    {/* Selection lines */}
                    <div className="divide-y divide-slate-50">
                        {summaryLines.map(({ label, value, step: targetStep, done }) => (
                            <div
                                key={label}
                                className={`flex items-center justify-between px-4 py-2.5 text-xs group transition-colors ${
                                    targetStep === currentStep ? 'bg-purple-50/60' : ''
                                }`}
                            >
                                <span className="text-slate-500 min-w-0">{label}</span>
                                <div className="flex items-center gap-1.5 ml-2">
                                    <span className={`font-medium text-right ${
                                        targetStep === currentStep
                                            ? 'text-purple-700'
                                            : done ? 'text-slate-800' : 'text-slate-400'
                                    }`}>
                                        {done || targetStep === currentStep || targetStep === 0 ? value : '—'}
                                    </span>
                                    {done && targetStep !== currentStep && (
                                        <button
                                            onClick={() => setStep(targetStep)}
                                            className="text-[10px] text-purple-500 opacity-0 group-hover:opacity-100 transition-opacity hover:text-purple-700 font-medium"
                                            data-testid={`edit-step-${targetStep}`}
                                        >
                                            Edit
                                        </button>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Price section */}
                    <div className="px-4 py-3 bg-slate-50 border-t border-slate-100">
                        {priceEstimate ? (
                            <>
                                <div className="flex items-baseline justify-between">
                                    <span className="text-xs text-slate-500">{t('wizard.per_unit', 'Per unit')}</span>
                                    <span className={`text-lg font-bold text-slate-900 font-display transition-opacity duration-150 ${isCalculating ? 'opacity-60' : 'opacity-100'}`}>
                                        &euro;{priceEstimate.unit_price_estimate?.toFixed(2)}
                                        <span className="text-xs font-medium text-slate-500 ml-1">/ unit</span>
                                    </span>
                                </div>
                                <div className="flex justify-between mt-0.5">
                                    <span className="text-[10px] text-slate-400">{t('wizard.total_estimate', 'Total estimate')}</span>
                                    <span className={`text-xs text-slate-500 transition-opacity duration-150 ${isCalculating ? 'opacity-60' : 'opacity-100'}`}>
                                        &euro;{priceEstimate.total_estimate?.toFixed(0)}
                                    </span>
                                </div>
                            </>
                        ) : (
                            // No estimate yet — show a subtle skeleton instead of the
                            // jarring "Calculating..." spinner. The first request usually
                            // resolves in <300ms; users barely see this state.
                            <>
                                <div className="flex items-baseline justify-between">
                                    <span className="text-xs text-slate-500">{t('wizard.per_unit', 'Per unit')}</span>
                                    <span className="inline-block h-5 w-16 rounded bg-slate-200/60 animate-pulse" aria-hidden="true" />
                                </div>
                                <div className="flex justify-between mt-0.5">
                                    <span className="text-[10px] text-slate-400">{t('wizard.total_estimate', 'Total estimate')}</span>
                                    <span className="inline-block h-3 w-10 rounded bg-slate-200/60 animate-pulse" aria-hidden="true" />
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    // ==================== STANDARD STEP LAYOUT WRAPPER ====================
    const StandardStepLayout = ({ children, currentStep }) => (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 w-full">
            <div className="lg:col-span-8">{children}</div>
            <div className="lg:col-span-4 hidden lg:block">
                <OrderSummaryPanel currentStep={currentStep} />
            </div>
            {/* Mobile: collapsed bottom summary (visible only on small screens) */}
            <div className="lg:hidden">
                <MobileOrderSummary currentStep={currentStep} />
            </div>
        </div>
    );

    // ==================== MOBILE ORDER SUMMARY (EXPANDABLE) ====================
    const MobileOrderSummary = ({ currentStep }) => {
        const [expanded, setExpanded] = useState(false);
        const displayFormat = specs.format?.replace(/"/g, '');

        return (
            <div className="fixed bottom-0 left-0 right-0 z-40 lg:hidden" data-testid="mobile-order-summary">
                <div className={`bg-white border-t border-slate-200 shadow-lg transition-all ${expanded ? 'max-h-[70vh]' : 'max-h-16'} overflow-hidden`}>
                    {/* Collapsed bar */}
                    <button
                        onClick={() => setExpanded(!expanded)}
                        className="w-full flex items-center justify-between px-4 py-3 text-sm"
                        data-testid="toggle-mobile-summary"
                    >
                        <div className="flex items-center gap-2">
                            <Package className="h-4 w-4 text-purple-500" />
                            <span className="font-medium text-slate-800">{displayFormat}" {getColorLabel(specs.color_mode)} &middot; {specs.quantity} units</span>
                        </div>
                        <div className="flex items-center gap-2">
                            {priceEstimate && (
                                <span className="font-bold text-slate-900 font-display">
                                    &euro;{priceEstimate.unit_price_estimate?.toFixed(2)}
                                    <span className="text-[10px] font-medium text-slate-500 ml-0.5">/ unit</span>
                                </span>
                            )}
                            <ChevronRight className={`h-4 w-4 text-slate-400 transition-transform ${expanded ? 'rotate-90' : '-rotate-90'}`} />
                        </div>
                    </button>

                    {/* Expanded content */}
                    {expanded && (
                        <div className="px-4 pb-4 border-t border-slate-100 overflow-y-auto max-h-[55vh]">
                            <OrderSummaryPanel currentStep={currentStep} />
                        </div>
                    )}
                </div>
            </div>
        );
    };

    // Step 0: Format & Quantity
    if (step === 0) {
        const formatAssets = getAssetsByCategory('format');
        const presets = [100, 250, 500, 1000];
        const currentQty = parseInt(specs.quantity) || 500;
        
        // Normalize format (handle both '12' and '12"')
        const normalizedFormat = specs.format?.replace(/"/g, '');
        
        // Validation: format must be selected and quantity must be >= 50
        // Validation: format must be selected and quantity must be >= 50
        const isStep0Valid = normalizedFormat && ['7', '10', '12'].includes(normalizedFormat) && currentQty >= 50;

        // Immersive Layout
        if (isImmersiveMode) {
            return (
                <ImmersiveContainer
                    onNext={() => setStep(1)}
                    canContinue={isStep0Valid}
                    currentStep={0}
                >
                    <div className="text-center mb-8">
                        <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight leading-tight text-slate-900 mb-2">
                            {steps[step].title}
                        </h2>
                        <p className="text-slate-500">{steps[step].subtitle}</p>
                    </div>

                    {/* Format Selection */}
                    <div className="mb-10">
                        <h3 className="text-sm font-semibold text-slate-700 mb-4 text-center">{t('wizard.choose_vinyl_format', 'Choose your vinyl format')}</h3>
                        <div className="grid grid-cols-3 gap-4 sm:gap-6 max-w-2xl mx-auto">
                            {['7', '10', '12'].map(format => {
                                const asset = formatAssets.find(a => a.option_id === format);
                                const isSelected = normalizedFormat === format;
                                return (
                                    <VisualOptionCard
                                        key={format}
                                        imageUrl={asset?.image_url}
                                        label={`${format}"`}
                                        description={format === '7' ? 'Single / EP' : format === '10' ? '10" EP' : 'Full Album'}
                                        isSelected={isSelected}
                                        onClick={() => setSpecs(prev => ({ 
                                            ...prev, 
                                            format,
                                            speed: format === '7' ? '45' : '33'
                                        }))}
                                        size="large"
                                    />
                                );
                            })}
                        </div>
                    </div>

                    {/* Quantity Selection */}
                    <div className="mb-6">
                        <h3 className="text-sm font-semibold text-slate-700 mb-4 text-center">{t('quick_order.how_many_copies', 'How many copies?')}</h3>
                        <div className="flex justify-center gap-3 flex-wrap">
                            {presets.map(qty => (
                                <QuantityCard
                                    key={qty}
                                    qty={qty}
                                    isSelected={currentQty === qty}
                                    onClick={() => setSpecs(prev => ({ ...prev, quantity: qty }))}
                                />
                            ))}
                        </div>
                        {/* Custom quantity input */}
                        <div className="mt-4 flex justify-center">
                            <div className="flex items-center gap-2 bg-background rounded-lg px-3 py-2">
                                <span className="text-xs text-slate-500">{t('wizard.custom_prefix', 'Custom:')}</span>
                                <input
                                    type="number"
                                    value={currentQty}
                                    onChange={(e) => setSpecs(prev => ({ ...prev, quantity: parseInt(e.target.value) || 100 }))}
                                    className="w-20 px-2 py-1 rounded border border-border text-center text-sm"
                                    min="50"
                                    max="10000"
                                />
                            </div>
                        </div>
                    </div>
                </ImmersiveContainer>
            );
        }

        // Standard Layout (Card-based)
        return (
            <StandardStepLayout currentStep={0}>
            <Card className="w-full">
                <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                        <div>
                            <Badge className="mb-2 bg-purple-100 text-purple-700">{t('wizard.guided_setup', 'Guided Setup')}</Badge>
                            <CardTitle>{steps[step].title}</CardTitle>
                            <CardDescription>{steps[step].subtitle}</CardDescription>
                        </div>
                        <Button variant="ghost" size="sm" onClick={onBack}>
                            Change mode
                        </Button>
                    </div>
                    <ProgressBar />
                </CardHeader>
                <CardContent className="space-y-6">
                    {/* Format selection */}
                    <div>
                        <Label className="text-sm font-medium mb-3 block">{t('wizard.vinyl_format', 'Vinyl Format')}</Label>
                        <div className="grid grid-cols-3 gap-4">
                            {['7', '10', '12'].map(format => {
                                const asset = formatAssets.find(a => a.option_id === format);
                                return (
                                    <VisualOption
                                        key={format}
                                        asset={asset || { label: `${format}"`, short_description: format === '7' ? 'Single' : format === '10' ? 'EP' : 'LP' }}
                                        isSelected={specs.format === format}
                                        onClick={() => setSpecs(prev => ({ 
                                            ...prev, 
                                            format,
                                            speed: format === '7' ? '45' : '33'
                                        }))}
                                        size="large"
                                    />
                                );
                            })}
                        </div>
                    </div>

                    {/* Quantity */}
                    <div>
                        <Label className="text-sm font-medium mb-3 block">{t('order.quantity_label', 'Quantity')}</Label>
                        <div className="flex gap-3 flex-wrap">
                            {presets.map(qty => (
                                <button
                                    key={qty}
                                    onClick={() => setSpecs(prev => ({ ...prev, quantity: qty }))}
                                    className={`px-5 py-3 rounded-lg border-2 transition-all ${
                                        currentQty === qty
                                            ? 'border-purple-500 bg-purple-50'
                                            : 'border-border hover:border-purple-300'
                                    }`}
                                >
                                    <span className="font-bold">{qty}</span>
                                    <span className="text-xs text-slate-500 ml-1">copies</span>
                                </button>
                            ))}
                            <Input
                                type="number"
                                value={!presets.includes(currentQty) ? specs.quantity : ''}
                                onChange={(e) => setSpecs(prev => ({ ...prev, quantity: e.target.value }))}
                                placeholder={t('wizard.custom', 'Custom')}
                                className="w-28"
                                min={50}
                            />
                        </div>
                    </div>

                    <PriceBar />

                    <div className="flex justify-end pt-4 border-t">
                        <Button onClick={() => setStep(1)} data-testid="visual-next">
                            Next: The Record <ChevronRight className="h-4 w-4 ml-1" />
                        </Button>
                    </div>
                </CardContent>
            </Card>
            </StandardStepLayout>
        );
    }

    // Step 1: Record (weight + color)
    if (step === 1) {
        const weightAssets = getAssetsByCategory('record_weight');
        const colorAssets = getAssetsByCategory('record_color');

        // Immersive Layout
        if (isImmersiveMode) {
            return (
                <ImmersiveContainer
                    onNext={() => setStep(2)}
                    onBack={() => setStep(0)}
                    canContinue={true}
                    currentStep={1}
                >
                    <div className="text-center mb-8">
                        <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight leading-tight text-slate-900 mb-2">
                            {steps[step].title}
                        </h2>
                        <p className="text-slate-500">{steps[step].subtitle}</p>
                    </div>

                    {/* Vinyl Weight */}
                    <div className="mb-10">
                        <h3 className="text-sm font-semibold text-slate-700 mb-2 text-center">{t('wizard.vinyl_weight', 'Vinyl Weight')}</h3>
                        <p className="text-xs text-slate-500 mb-4 text-center">Heavier vinyl = more premium feel, less warping</p>
                        <div className="grid grid-cols-2 gap-4 sm:gap-5 max-w-lg mx-auto">
                            {[
                                { id: '140g', label: 'Standard (140g)', desc: 'Industry standard, great sound' },
                                { id: '180g', label: 'Heavyweight (180g)', desc: 'Premium feel, reduced warping' },
                            ].map(weight => {
                                const asset = weightAssets.find(a => a.option_id === weight.id);
                                return (
                                    <VisualOptionCard
                                        key={weight.id}
                                        imageUrl={asset?.image_url}
                                        label={weight.label}
                                        description={asset?.short_description || weight.desc}
                                        isSelected={specs.vinyl_weight === weight.id}
                                        onClick={() => setSpecs(prev => ({ ...prev, vinyl_weight: weight.id }))}
                                        size="default"
                                    />
                                );
                            })}
                        </div>
                    </div>

                    {/* Vinyl Color */}
                    <div className="mb-6">
                        <h3 className="text-sm font-semibold text-slate-700 mb-2 text-center">{t('wizard.vinyl_color', 'Vinyl Color')}</h3>
                        <p className="text-xs text-slate-500 mb-4 text-center">Black offers best audio fidelity; colors make your release stand out</p>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-3xl mx-auto">
                            {[
                                { id: 'black', label: 'Classic Black', desc: 'Best audio fidelity' },
                                { id: 'color', label: 'Solid Color', desc: 'Custom colors' },
                                { id: 'splatter', label: 'Splatter / Marble', desc: 'Unique patterns' },
                                { id: 'picture', label: 'Picture Disc', desc: 'Artwork on vinyl' },
                            ].map(color => {
                                const asset = colorAssets.find(a => a.option_id === color.id);
                                return (
                                    <VisualOptionCard
                                        key={color.id}
                                        imageUrl={asset?.image_url}
                                        label={color.label}
                                        description={asset?.short_description || color.desc}
                                        isSelected={specs.color_mode === color.id}
                                        onClick={() => setSpecs(prev => ({ ...prev, color_mode: color.id }))}
                                        size="small"
                                    />
                                );
                            })}
                        </div>
                    </div>

                    {/* Center Label */}
                    <div className="mb-10">
                        <h3 className="text-sm font-semibold text-slate-700 mb-2 text-center">{t('wizard.center_label', 'Center Label')}</h3>
                        <p className="text-xs text-slate-500 mb-4 text-center">{t('wizard.center_label_desc', 'The printed label in the center of your vinyl')}</p>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-3xl mx-auto">
                            {[
                                { id: '4color', label: '4-Colour Print', desc: 'Full color artwork' },
                                { id: 'black', label: 'Black Print', desc: 'Single color, classic look' },
                                { id: 'unprinted', label: 'Unprinted', desc: 'Blank white label' },
                                { id: 'none', label: 'No Label', desc: 'Clean disc look' },
                            ].map(label => {
                                const asset = getAssetsByCategory('center_label').find(a => a.option_id === label.id);
                                return (
                                    <VisualOptionCard
                                        key={label.id}
                                        imageUrl={asset?.image_url}
                                        label={label.label}
                                        description={asset?.short_description || label.desc}
                                        isSelected={specs.center_label === label.id}
                                        onClick={() => setSpecs(prev => ({ ...prev, center_label: label.id }))}
                                        size="small"
                                    />
                                );
                            })}
                        </div>
                    </div>

                    {/* Runout Engraving */}
                    <div className="mb-6">
                        <h3 className="text-sm font-semibold text-slate-700 mb-2 text-center">{t('wizard.runout_engraving', 'Runout Engraving')}</h3>
                        <p className="text-xs text-slate-500 mb-4 text-center">{t('wizard.runout_engraving_desc', "Custom text etched in the vinyl's runout groove area")}</p>
                        <div className="flex justify-center gap-4 mb-4">
                            <ToggleOptionCard
                                label="No engraving"
                                isSelected={specs.runout_engraving === 'none'}
                                onClick={() => setSpecs(prev => ({ ...prev, runout_engraving: 'none', engraving_text: '' }))}
                            />
                            <ToggleOptionCard
                                label="Add engraving"
                                isSelected={specs.runout_engraving === 'engraving'}
                                onClick={() => setSpecs(prev => ({ ...prev, runout_engraving: 'engraving' }))}
                            />
                        </div>
                        {specs.runout_engraving === 'engraving' && (
                            <div className="max-w-md mx-auto">
                                <label className="block text-xs text-slate-500 mb-2 text-center">
                                    Enter your engraving text (max 40 characters)
                                </label>
                                <input
                                    type="text"
                                    value={specs.engraving_text || ''}
                                    onChange={(e) => {
                                        if (e.target.value.length <= 40) {
                                            setSpecs(prev => ({ ...prev, engraving_text: e.target.value }));
                                        }
                                    }}
                                    placeholder="e.g., Made with love in Copenhagen"
                                    className="w-full px-4 py-2 rounded-xl border-2 border-border focus:border-purple-500 focus:outline-none text-center"
                                    maxLength={40}
                                />
                                <p className="text-xs text-slate-400 mt-1 text-center">
                                    {specs.engraving_text?.length || 0}/40 characters
                                </p>
                            </div>
                        )}
                    </div>
                </ImmersiveContainer>
            );
        }

        return (
            <StandardStepLayout currentStep={1}>
            <Card className="w-full">
                <CardHeader className="pb-2">
                    <Badge className="mb-2 bg-purple-100 text-purple-700">{t('wizard.guided_setup', 'Guided Setup')}</Badge>
                    <CardTitle>{steps[step].title}</CardTitle>
                    <CardDescription>{steps[step].subtitle}</CardDescription>
                    <ProgressBar />
                </CardHeader>
                <CardContent className="space-y-6">
                    {/* Weight */}
                    <div>
                        <Label className="text-sm font-medium mb-2 block">{t('wizard.vinyl_weight', 'Vinyl Weight')}</Label>
                        <p className="text-xs text-slate-500 mb-3">Heavier vinyl = more premium feel, less warping</p>
                        <div className="grid grid-cols-2 gap-4 max-w-md">
                            {['140g', '180g'].map(weight => {
                                const asset = weightAssets.find(a => a.option_id === weight);
                                return (
                                    <VisualOption
                                        key={weight}
                                        asset={asset || { label: weight, short_description: weight === '140g' ? 'Standard' : 'Heavyweight' }}
                                        isSelected={specs.vinyl_weight === weight}
                                        onClick={() => setSpecs(prev => ({ ...prev, vinyl_weight: weight }))}
                                        size="large"
                                    />
                                );
                            })}
                        </div>
                    </div>

                    {/* Color */}
                    <div>
                        <Label className="text-sm font-medium mb-2 block">{t('wizard.vinyl_color', 'Vinyl Color')}</Label>
                        <p className="text-xs text-slate-500 mb-3">Black offers best audio fidelity; colors make your release stand out</p>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                            {[
                                { id: 'black', label: 'Classic Black', desc: 'Best audio quality' },
                                { id: 'color', label: 'Solid Color', desc: 'Stand out' },
                                { id: 'splatter', label: 'Splatter/Marble', desc: 'Unique patterns' },
                                { id: 'picture', label: 'Picture Disc', desc: 'Max visual impact' },
                            ].map(color => {
                                const asset = colorAssets.find(a => a.option_id === color.id) || color;
                                return (
                                    <VisualOption
                                        key={color.id}
                                        asset={asset}
                                        isSelected={specs.color_mode === color.id}
                                        onClick={() => setSpecs(prev => ({ ...prev, color_mode: color.id }))}
                                    />
                                );
                            })}
                        </div>
                    </div>

                    <PriceBar />

                    <div className="flex justify-between pt-4 border-t">
                        <Button variant="outline" onClick={() => setStep(0)}>
                            <ChevronLeft className="h-4 w-4 mr-1" /> Back
                        </Button>
                        <Button onClick={() => setStep(2)}>
                            Next: Inner Protection <ChevronRight className="h-4 w-4 ml-1" />
                        </Button>
                    </div>
                </CardContent>
            </Card>
            </StandardStepLayout>
        );
    }

    // Step 2: Inner Sleeve
    if (step === 2) {
        const innerAssets = getAssetsByCategory('inner_sleeve');

        // Immersive Layout
        if (isImmersiveMode) {
            return (
                <ImmersiveContainer
                    onNext={() => setStep(3)}
                    onBack={() => setStep(1)}
                    canContinue={true}
                    currentStep={2}
                >
                    <div className="text-center mb-8">
                        <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight leading-tight text-slate-900 mb-2">
                            {steps[step].title}
                        </h2>
                        <p className="text-slate-500">{steps[step].subtitle}</p>
                    </div>

                    <p className="text-sm text-slate-600 bg-purple-50/50 p-4 rounded-xl text-center mb-8 max-w-2xl mx-auto">
                        The inner sleeve protects your vinyl from dust and static. Better protection = longer vinyl life.
                    </p>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-3xl mx-auto">
                        {[
                            { id: 'none', label: 'No Inner', desc: 'Budget option' },
                            { id: 'paper', label: 'Plain Paper', desc: 'Basic protection' },
                            { id: 'poly_lined', label: 'Poly-Lined', desc: 'Anti-static' },
                            { id: 'printed', label: 'Printed Inner', desc: 'Custom artwork' },
                        ].map(inner => {
                            const asset = innerAssets.find(a => a.option_id === inner.id);
                            return (
                                <VisualOptionCard
                                    key={inner.id}
                                    imageUrl={asset?.image_url}
                                    label={inner.label}
                                    description={asset?.short_description || inner.desc}
                                    isSelected={specs.inner_sleeve === inner.id}
                                    onClick={() => setSpecs(prev => ({ ...prev, inner_sleeve: inner.id }))}
                                    size="default"
                                />
                            );
                        })}
                    </div>
                </ImmersiveContainer>
            );
        }

        return (
            <StandardStepLayout currentStep={2}>
            <Card className="w-full">
                <CardHeader className="pb-2">
                    <Badge className="mb-2 bg-purple-100 text-purple-700">{t('wizard.guided_setup', 'Guided Setup')}</Badge>
                    <CardTitle>{steps[step].title}</CardTitle>
                    <CardDescription>{steps[step].subtitle}</CardDescription>
                    <ProgressBar />
                </CardHeader>
                <CardContent className="space-y-6">
                    <p className="text-sm text-slate-600 bg-purple-50 p-3 rounded-lg">
                        The inner sleeve protects your vinyl from dust and static. Better protection = longer vinyl life.
                    </p>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        {[
                            { id: 'none', label: 'No Inner', desc: 'Budget option' },
                            { id: 'paper', label: 'Plain Paper', desc: 'Basic protection' },
                            { id: 'poly_lined', label: 'Poly-Lined', desc: 'Anti-static, recommended' },
                            { id: 'printed', label: 'Printed Inner', desc: 'Custom artwork/lyrics' },
                        ].map(inner => {
                            const asset = innerAssets.find(a => a.option_id === inner.id) || inner;
                            return (
                                <VisualOption
                                    key={inner.id}
                                    asset={asset}
                                    isSelected={specs.inner_sleeve === inner.id}
                                    onClick={() => setSpecs(prev => ({ ...prev, inner_sleeve: inner.id }))}
                                    size="large"
                                />
                            );
                        })}
                    </div>

                    <PriceBar />

                    <div className="flex justify-between pt-4 border-t">
                        <Button variant="outline" onClick={() => setStep(1)}>
                            <ChevronLeft className="h-4 w-4 mr-1" /> Back
                        </Button>
                        <Button onClick={() => setStep(3)}>
                            Next: Main Cover <ChevronRight className="h-4 w-4 ml-1" />
                        </Button>
                    </div>
                </CardContent>
            </Card>
            </StandardStepLayout>
        );
    }

    // Step 3: Cover (outer sleeve)
    if (step === 3) {
        const coverAssets = getAssetsByCategory('cover');

        // Immersive Layout
        if (isImmersiveMode) {
            return (
                <ImmersiveContainer
                    onNext={() => setStep(4)}
                    onBack={() => setStep(2)}
                    canContinue={true}
                    currentStep={3}
                >
                    <div className="text-center mb-8">
                        <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight leading-tight text-slate-900 mb-2">
                            {steps[step].title}
                        </h2>
                        <p className="text-slate-500">{steps[step].subtitle}</p>
                    </div>

                    <p className="text-sm text-slate-600 bg-purple-50/50 p-4 rounded-xl text-center mb-8 max-w-2xl mx-auto">
                        Your cover is the first thing people see. It's your canvas for artwork and branding.
                    </p>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-3xl mx-auto">
                        {[
                            { id: 'none', label: 'No Sleeve', desc: 'Just the record', sleeve_package: 'none' },
                            { id: 'standard', label: 'Single Sleeve', desc: 'Classic cover', sleeve_package: 'standard' },
                            { id: 'gatefold', label: 'Gatefold', desc: 'Opens like a book', sleeve_package: 'gatefold' },
                            { id: 'tip_on', label: 'Tip-On Deluxe', desc: 'Premium finish', sleeve_package: 'printed' },
                        ].map(cover => {
                            const asset = coverAssets.find(a => a.option_id === cover.id);
                            return (
                                <VisualOptionCard
                                    key={cover.id}
                                    imageUrl={asset?.image_url}
                                    label={cover.label}
                                    description={asset?.short_description || cover.desc}
                                    isSelected={specs.sleeve_package === cover.sleeve_package}
                                    onClick={() => setSpecs(prev => ({ ...prev, sleeve_package: cover.sleeve_package }))}
                                    size="default"
                                />
                            );
                        })}
                    </div>
                </ImmersiveContainer>
            );
        }

        return (
            <StandardStepLayout currentStep={3}>
            <Card className="w-full">
                <CardHeader className="pb-2">
                    <Badge className="mb-2 bg-purple-100 text-purple-700">{t('wizard.guided_setup', 'Guided Setup')}</Badge>
                    <CardTitle>{steps[step].title}</CardTitle>
                    <CardDescription>{steps[step].subtitle}</CardDescription>
                    <ProgressBar />
                </CardHeader>
                <CardContent className="space-y-6">
                    <p className="text-sm text-slate-600 bg-purple-50 p-3 rounded-lg">
                        Your cover is the first thing people see. It's your canvas for artwork and branding.
                    </p>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        {[
                            { id: 'none', label: 'No Sleeve', desc: 'Just the record', sleeve_package: 'none' },
                            { id: 'standard', label: 'Single Sleeve', desc: 'Classic album cover', sleeve_package: 'standard' },
                            { id: 'gatefold', label: 'Gatefold', desc: 'Opens like a book', sleeve_package: 'gatefold' },
                            { id: 'tip_on', label: 'Tip-On Deluxe', desc: 'Premium heavyweight', sleeve_package: 'printed' },
                        ].map(cover => {
                            const asset = coverAssets.find(a => a.option_id === cover.id) || cover;
                            return (
                                <VisualOption
                                    key={cover.id}
                                    asset={asset}
                                    isSelected={specs.sleeve_package === cover.sleeve_package}
                                    onClick={() => setSpecs(prev => ({ ...prev, sleeve_package: cover.sleeve_package }))}
                                    size="large"
                                />
                            );
                        })}
                    </div>

                    <PriceBar />

                    <div className="flex justify-between pt-4 border-t">
                        <Button variant="outline" onClick={() => setStep(2)}>
                            <ChevronLeft className="h-4 w-4 mr-1" /> Back
                        </Button>
                        <Button onClick={() => setStep(4)}>
                            Next: Extras <ChevronRight className="h-4 w-4 ml-1" />
                        </Button>
                    </div>
                </CardContent>
            </Card>
            </StandardStepLayout>
        );
    }

    // Step 4: Extras
    if (step === 4) {
        const extrasAssets = getAssetsByCategory('extras');
        const testPressings = parseInt(specs.test_pressings) || 0;
        const hasPolyOuter = specs.outer_protection === 'pvc';

        // Immersive Layout
        if (isImmersiveMode) {
            return (
                <ImmersiveContainer
                    onNext={() => setStep(5)}
                    onBack={() => setStep(3)}
                    canContinue={true}
                    currentStep={4}
                >
                    <div className="text-center mb-8">
                        <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight leading-tight text-slate-900 mb-2">
                            {steps[step].title}
                        </h2>
                        <p className="text-slate-500">{steps[step].subtitle}</p>
                    </div>

                    <p className="text-sm text-slate-600 bg-purple-50/50 p-4 rounded-xl text-center mb-8 max-w-2xl mx-auto">
                        Optional additions to enhance your release.
                    </p>

                    {/* Test Pressings */}
                    <div className="mb-10">
                        <h3 className="text-sm font-semibold text-slate-700 mb-2 text-center">{t('wizard.test_pressings', 'Test Pressings')}</h3>
                        <p className="text-xs text-slate-500 mb-4 text-center">{t('wizard.approve_quality', 'Approve quality before the full run')}</p>
                        <div className="flex justify-center gap-3 flex-wrap">
                            {[
                                { qty: 0, label: 'None' },
                                { qty: 5, label: '5 copies' },
                                { qty: 10, label: '10 copies' },
                                { qty: 20, label: '20 copies' },
                            ].map(({ qty, label }) => (
                                <ToggleOptionCard
                                    key={qty}
                                    label={label}
                                    isSelected={testPressings === qty}
                                    onClick={() => setSpecs(prev => ({ ...prev, test_pressings: qty.toString() }))}
                                />
                            ))}
                        </div>
                    </div>

                    {/* Outer Protection */}
                    <div className="mb-6">
                        <h3 className="text-sm font-semibold text-slate-700 mb-2 text-center">{t('wizard.outer_poly_sleeve', 'Outer Poly Sleeve')}</h3>
                        <p className="text-xs text-slate-500 mb-4 text-center">{t('wizard.poly_sleeve_desc', 'Protects your cover from scratches and dust')}</p>
                        <div className="flex justify-center gap-4">
                            <ToggleOptionCard
                                label="No outer sleeve"
                                isSelected={!hasPolyOuter}
                                onClick={() => setSpecs(prev => ({ ...prev, outer_protection: 'none' }))}
                            />
                            <ToggleOptionCard
                                label="Add poly sleeve"
                                isSelected={hasPolyOuter}
                                onClick={() => setSpecs(prev => ({ ...prev, outer_protection: 'pvc' }))}
                            />
                        </div>
                    </div>
                </ImmersiveContainer>
            );
        }

        return (
            <StandardStepLayout currentStep={4}>
            <Card className="w-full">
                <CardHeader className="pb-2">
                    <Badge className="mb-2 bg-purple-100 text-purple-700">{t('wizard.guided_setup', 'Guided Setup')}</Badge>
                    <CardTitle>{steps[step].title}</CardTitle>
                    <CardDescription>{steps[step].subtitle}</CardDescription>
                    <ProgressBar />
                </CardHeader>
                <CardContent className="space-y-6">
                    <p className="text-sm text-slate-600 bg-purple-50 p-3 rounded-lg">
                        Optional additions to enhance your release.
                    </p>

                    {/* Test Pressings */}
                    <div>
                        <Label className="text-sm font-medium mb-2 block">{t('wizard.test_pressings', 'Test Pressings')}</Label>
                        <p className="text-xs text-slate-500 mb-3">{t('wizard.approve_quality', 'Approve quality before the full run')}</p>
                        <div className="flex gap-2 flex-wrap">
                            {[0, 5, 10, 20].map(qty => (
                                <button
                                    key={qty}
                                    onClick={() => setSpecs(prev => ({ ...prev, test_pressings: qty.toString() }))}
                                    className={`px-4 py-2 rounded-lg border-2 transition-all ${
                                        testPressings === qty
                                            ? 'border-purple-500 bg-purple-50'
                                            : 'border-border hover:border-purple-300'
                                    }`}
                                >
                                    {qty === 0 ? 'None' : `${qty} copies`}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Outer protection */}
                    <div>
                        <Label className="text-sm font-medium mb-2 block">{t('wizard.outer_poly_sleeve', 'Outer Poly Sleeve')}</Label>
                        <p className="text-xs text-slate-500 mb-3">{t('wizard.poly_sleeve_desc', 'Protects your cover from scratches and dust')}</p>
                        <div className="flex gap-3">
                            <button
                                onClick={() => setSpecs(prev => ({ ...prev, outer_protection: 'none' }))}
                                className={`px-4 py-3 rounded-lg border-2 transition-all ${
                                    !hasPolyOuter
                                        ? 'border-purple-500 bg-purple-50'
                                        : 'border-border hover:border-purple-300'
                                }`}
                            >
                                No outer sleeve
                            </button>
                            <button
                                onClick={() => setSpecs(prev => ({ ...prev, outer_protection: 'pvc' }))}
                                className={`px-4 py-3 rounded-lg border-2 transition-all ${
                                    hasPolyOuter
                                        ? 'border-purple-500 bg-purple-50'
                                        : 'border-border hover:border-purple-300'
                                }`}
                            >
                                Add poly sleeve
                            </button>
                        </div>
                    </div>

                    <PriceBar />

                    <div className="flex justify-between pt-4 border-t">
                        <Button variant="outline" onClick={() => setStep(3)}>
                            <ChevronLeft className="h-4 w-4 mr-1" /> Back
                        </Button>
                        <Button onClick={() => setStep(5)}>
                            Review your build <ChevronRight className="h-4 w-4 ml-1" />
                        </Button>
                    </div>
                </CardContent>
            </Card>
            </StandardStepLayout>
        );
    }

    // Step 5: Review
    // Normalize format for display
    const displayFormat = specs.format?.replace(/"/g, '');
    
    // Immersive Layout for Review
    if (isImmersiveMode) {
        return (
            <ImmersiveContainer
                onNext={() => onComplete && onComplete(specs, priceEstimate)}
                onBack={() => setStep(4)}
                canContinue={!!priceEstimate}
                isLastStep={true}
                currentStep={5}
            >
                <div className="text-center mb-8">
                    <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight leading-tight text-slate-900 mb-2">
                        {steps[step].title}
                    </h2>
                    <p className="text-slate-500">{steps[step].subtitle}</p>
                </div>

                {/* Summary Card */}
                <div className="bg-gradient-to-br from-purple-50 to-slate-50 rounded-2xl p-6 sm:p-8 border border-purple-200 max-w-2xl mx-auto">
                    <h3 className="text-lg font-semibold text-slate-900 mb-4">{t('wizard.your_configuration_h3', 'Your Configuration')}</h3>
                    <div className="grid grid-cols-2 gap-x-8 gap-y-3 text-sm">
                        <div className="flex justify-between">
                            <span className="text-slate-500">{t('wizard.summary.format', 'Format')}</span>
                            <span className="font-medium">{displayFormat}" vinyl</span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-slate-500">{t('order.quantity_label', 'Quantity')}</span>
                            <span className="font-medium">{specs.quantity} copies</span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-slate-500">{t('wizard.summary.weight', 'Weight')}</span>
                            <span className="font-medium">{specs.vinyl_weight}</span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-slate-500">Color</span>
                            <span className="font-medium">{specs.color_mode === 'black' ? 'Black' : specs.color_mode === 'color' ? 'Colored' : specs.color_mode}</span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-slate-500">{t('wizard.summary.center_label', 'Center label')}</span>
                            <span className="font-medium">
                                {specs.center_label === '4color' ? '4-Colour' : 
                                 specs.center_label === 'black' ? 'Black print' : 
                                 specs.center_label === 'unprinted' ? 'Unprinted' : 
                                 specs.center_label === 'none' ? 'No label' : specs.center_label}
                            </span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-slate-500">{t('wizard.summary.engraving', 'Engraving')}</span>
                            <span className="font-medium">
                                {specs.runout_engraving === 'engraving' 
                                    ? (specs.engraving_text ? `"${specs.engraving_text.slice(0, 15)}${specs.engraving_text.length > 15 ? '...' : ''}"` : 'Yes') 
                                    : 'No'}
                            </span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-slate-500">{t('wizard.summary.inner_sleeve', 'Inner sleeve')}</span>
                            <span className="font-medium">{specs.inner_sleeve || 'None'}</span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-slate-500">Cover</span>
                            <span className="font-medium">{specs.sleeve_package || 'None'}</span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-slate-500">{t('wizard.summary.test_pressings', 'Test pressings')}</span>
                            <span className="font-medium">{specs.test_pressings || 0}</span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-slate-500">{t('wizard.summary.outer_protection', 'Outer protection')}</span>
                            <span className="font-medium">{specs.outer_protection === 'pvc' ? 'Yes' : 'No'}</span>
                        </div>
                    </div>
                </div>

                <p className="text-xs text-slate-500 text-center mt-6 max-w-xl mx-auto">
                    Final price may vary based on artwork complexity. You'll receive a detailed quote after submitting.
                </p>
            </ImmersiveContainer>
        );
    }

    return (
        <StandardStepLayout currentStep={5}>
        <Card className="w-full">
            <CardHeader className="pb-2">
                <Badge className="mb-2 bg-purple-100 text-purple-700">Guided Setup</Badge>
                <CardTitle>{steps[step].title}</CardTitle>
                <CardDescription>{steps[step].subtitle}</CardDescription>
                <ProgressBar />
            </CardHeader>
            <CardContent className="space-y-6">
                {/* Summary */}
                <div className="bg-gradient-to-br from-purple-50 to-slate-50 rounded-2xl p-6 border border-purple-200">
                    <div className="grid grid-cols-2 gap-x-8 gap-y-3 text-sm">
                        <div className="flex justify-between">
                            <span className="text-slate-500">{t('wizard.summary.format', 'Format')}</span>
                            <span className="font-medium">{specs.format}" vinyl</span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-slate-500">{t('order.quantity_label', 'Quantity')}</span>
                            <span className="font-medium">{specs.quantity} copies</span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-slate-500">{t('wizard.summary.weight', 'Weight')}</span>
                            <span className="font-medium">{specs.vinyl_weight}</span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-slate-500">Color</span>
                            <span className="font-medium">{specs.color_mode === 'black' ? 'Black' : 'Colored'}</span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-slate-500">{t('wizard.summary.inner_sleeve', 'Inner sleeve')}</span>
                            <span className="font-medium">{specs.inner_sleeve || 'None'}</span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-slate-500">Cover</span>
                            <span className="font-medium">{specs.sleeve_package || 'None'}</span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-slate-500">{t('wizard.summary.test_pressings', 'Test pressings')}</span>
                            <span className="font-medium">{specs.test_pressings || 0}</span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-slate-500">{t('wizard.summary.outer_protection', 'Outer protection')}</span>
                            <span className="font-medium">{specs.outer_protection === 'pvc' ? 'Yes' : 'No'}</span>
                        </div>
                    </div>

                    <div className="mt-6 pt-4 border-t border-purple-200 flex items-center justify-between">
                        <span className="text-slate-600">{t('flow.estimated_price', 'Estimated price')}</span>
                        {priceEstimate ? (
                            <div className="text-right">
                                <span className="text-3xl font-bold text-purple-600 font-display">
                                    {formatInCustomerCurrency(priceEstimate.unit_price_estimate, 2)}
                                    <span className="text-base font-medium text-purple-500 ml-1">/ unit</span>
                                </span>
                                <span className="block text-xs text-slate-500 mt-1">
                                    {formatInCustomerCurrency(priceEstimate.total_estimate, 0)} total
                                </span>
                            </div>
                        ) : isCalculating ? (
                            <Loader2 className="h-6 w-6 animate-spin text-purple-500" />
                        ) : (
                            <span className="text-slate-400">—</span>
                        )}
                    </div>
                </div>

                <p className="text-xs text-slate-500 text-center">
                    Final price may vary based on artwork complexity. You'll receive a detailed quote after submitting.
                </p>

                <div className="flex justify-between pt-4 border-t">
                    <Button variant="outline" onClick={() => setStep(4)}>
                        <ChevronLeft className="h-4 w-4 mr-1" /> Back
                    </Button>
                    <Button 
                        onClick={() => onComplete && onComplete(specs, priceEstimate)}
                        disabled={!priceEstimate}
                        className="bg-purple-600 hover:bg-purple-700"
                        data-testid="visual-confirm"
                    >
                        <Check className="h-4 w-4 mr-2" />
                        Confirm Configuration
                    </Button>
                </div>
            </CardContent>
        </Card>
        </StandardStepLayout>
    );
}

// ==================== EXPERT MODE ====================
// ==================== EXPERT MODE - FLOW DRIVEN ====================
// Section configuration for collapsible groups
const EXPERT_SECTIONS = {
    format: { label: "Format & Speed", icon: Disc, stepIds: ["format", "speed"] },
    vinyl: { label: "Vinyl Options", icon: Palette, stepIds: ["vinyl_color", "vinyl_weight", "test_pressings"] },
    label: { label: "Center Label", icon: Disc, stepIds: ["center_label"] },
    sleeve: { label: "Inner Sleeve", icon: Package, stepIds: ["inner_sleeve"] },
    cover: { label: "Cover/Jacket", icon: Package, stepIds: ["cover_type", "cover_finish"] },
    extras: { label: "Extras & Finishing", icon: Sparkles, stepIds: ["shrink_wrap", "sticker", "insert"] }
};

function ExpertMode({
    specs,
    setSpecs,
    onBack,
    onComplete
}) {
    const { t, language } = useTranslation();
    const [flow, setFlow] = useState(null);
    const [assets, setAssets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selections, setSelections] = useState({});
    const [priceEstimate, setPriceEstimate] = useState(null);
    const [isCalculating, setIsCalculating] = useState(false);
    const [expandedSections, setExpandedSections] = useState(
        Object.keys(EXPERT_SECTIONS).reduce((acc, key) => ({ ...acc, [key]: true }), {})
    );

    // Fetch Expert flow on mount + re-fetch assets when language changes.
    useEffect(() => {
        const fetchExpertFlow = async () => {
            try {
                setLoading(true);
                const langQ = language && language !== 'en' ? `?lang=${encodeURIComponent(language)}` : '';
                const [flowRes, assetsRes] = await Promise.all([
                    fetch(`${API_URL}/api/product-config/flows/by-mode/expert`),
                    fetch(`${API_URL}/api/visual-builder/assets${langQ}`)
                ]);
                
                if (!flowRes.ok) {
                    throw new Error('Failed to load Expert configuration');
                }
                
                const flowData = await flowRes.json();
                if (!flowData || !flowData.id) {
                    throw new Error('Expert configuration is not available yet. Please contact support.');
                }
                
                setFlow(flowData);
                
                if (assetsRes.ok) {
                    const assetsData = await assetsRes.json();
                    setAssets(assetsData);
                }
                
                // Initialize selections with defaults
                const initialSelections = {};
                flowData.steps?.forEach(step => {
                    const defaultOption = step.options?.find(o => o.is_default);
                    if (defaultOption) {
                        initialSelections[step.step_id] = [defaultOption.option_id];
                    }
                });
                setSelections(initialSelections);
                
            } catch (err) {
                console.error('Expert flow load error:', err);
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };
        fetchExpertFlow();
    }, [language]);

    // Calculate price when selections change
    useEffect(() => {
        if (!flow || Object.keys(selections).length === 0) return;
        
        const calculatePrice = async () => {
            setIsCalculating(true);
            try {
                const lineItems = [];
                
                // Build line items from selections
                flow.steps?.forEach(step => {
                    const selectedIds = selections[step.step_id] || [];
                    selectedIds.forEach(optionId => {
                        const option = step.options?.find(o => o.option_id === optionId);
                        if (option?.pricing_mapping && !option.is_included_in_base) {
                            lineItems.push({
                                sales_sku: option.pricing_mapping.sales_sku,
                                quantity_multiplier: option.pricing_mapping.quantity_multiplier || 1
                            });
                        }
                    });
                });
                
                if (lineItems.length === 0) {
                    setPriceEstimate(null);
                    return;
                }
                
                const res = await fetch(`${API_URL}/api/sales/quote`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        quantity: parseInt(specs.quantity) || 500,
                        line_items: lineItems
                    })
                });
                
                if (res.ok) {
                    const data = await res.json();
                    setPriceEstimate({
                        total_estimate: data.total_estimate || 0,
                        unit_price_estimate: data.unit_price_estimate || 0
                    });
                }
            } catch (err) {
                console.error('Price calculation error:', err);
            } finally {
                setIsCalculating(false);
            }
        };
        
        const timer = setTimeout(calculatePrice, 300);
        return () => clearTimeout(timer);
    }, [flow, selections, specs.quantity]);

    // Get asset label for an option
    const getOptionLabel = (step, optionId) => {
        const asset = assets.find(
            a => a.category_id === step.category_id && a.option_id === optionId
        );
        if (asset) return asset.label;
        // Fallback: format the option_id
        return optionId.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
    };

    // Handle selection change
    const handleSelectionChange = (stepId, value) => {
        setSelections(prev => ({
            ...prev,
            [stepId]: [value]
        }));
    };

    // Toggle section expand/collapse
    const toggleSection = (sectionKey) => {
        setExpandedSections(prev => ({
            ...prev,
            [sectionKey]: !prev[sectionKey]
        }));
    };

    // Build flow selections for completion
    const buildFlowConfig = () => {
        return {
            _flow_id: flow.id,
            _flow_version: flow.version,
            _flow_selections: selections,
            ...specs
        };
    };

    // Loading state
    if (loading) {
        return (
            <Card className="w-full">
                <CardContent className="py-12 flex flex-col items-center justify-center">
                    <Loader2 className="h-8 w-8 animate-spin text-slate-400 mb-4" />
                    <p className="text-slate-500">{t('wizard.loading_expert', 'Loading Expert configuration...')}</p>
                </CardContent>
            </Card>
        );
    }

    // Error state - no fallback to hardcoded options
    if (error || !flow) {
        return (
            <Card className="w-full">
                <CardContent className="py-12 text-center">
                    <AlertCircle className="h-12 w-12 text-amber-500 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-slate-900 mb-2">{t('wizard.expert_unavailable', 'Expert Mode Unavailable')}</h3>
                    <p className="text-sm text-slate-500 mb-4">
                        {error || 'Expert configuration is not available yet. Please contact support or use Visual Builder mode.'}
                    </p>
                    <Button variant="outline" onClick={onBack}>
                        <ChevronLeft className="h-4 w-4 mr-1" />
                        Back to Mode Selection
                    </Button>
                </CardContent>
            </Card>
        );
    }

    // Group steps by section
    const stepsBySection = {};
    Object.entries(EXPERT_SECTIONS).forEach(([sectionKey, section]) => {
        stepsBySection[sectionKey] = flow.steps?.filter(
            step => section.stepIds.includes(step.step_id)
        ) || [];
    });

    return (
        <Card className="w-full">
            <CardHeader className="pb-4">
                <div className="flex items-center justify-between">
                    <div>
                        <Badge className="mb-2 bg-slate-700 text-white">Expert Mode</Badge>
                        <CardTitle className="flex items-center gap-2">
                            <Settings2 className="h-5 w-5 text-slate-600" />
                            Full Configuration
                        </CardTitle>
                        <CardDescription>
                            Complete control over all product options
                        </CardDescription>
                    </div>
                    <Button variant="ghost" size="sm" onClick={onBack}>
                        Change mode
                    </Button>
                </div>
            </CardHeader>

            <CardContent className="space-y-4">
                {/* Quantity (separate from flow) */}
                <div className="bg-slate-50 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                        <div>
                            <Label className="text-sm font-medium">Quantity</Label>
                            <p className="text-xs text-slate-500">Number of copies to produce</p>
                        </div>
                        <Input
                            type="number"
                            value={specs.quantity || 500}
                            onChange={(e) => setSpecs(prev => ({ ...prev, quantity: e.target.value }))}
                            className="w-32"
                            min={50}
                            data-testid="expert-quantity"
                        />
                    </div>
                </div>

                {/* Collapsible Sections */}
                {Object.entries(EXPERT_SECTIONS).map(([sectionKey, sectionConfig]) => {
                    const sectionSteps = stepsBySection[sectionKey];
                    if (!sectionSteps || sectionSteps.length === 0) return null;
                    
                    const SectionIcon = sectionConfig.icon;
                    const isExpanded = expandedSections[sectionKey];
                    
                    return (
                        <div 
                            key={sectionKey}
                            className="border rounded-lg overflow-hidden"
                        >
                            {/* Section Header */}
                            <button
                                onClick={() => toggleSection(sectionKey)}
                                className="w-full flex items-center justify-between p-3 bg-slate-50 hover:bg-slate-100 transition-colors"
                                data-testid={`expert-section-${sectionKey}`}
                            >
                                <div className="flex items-center gap-2">
                                    <SectionIcon className="h-4 w-4 text-slate-500" />
                                    <span className="font-medium text-slate-700">{sectionConfig.label}</span>
                                </div>
                                <ChevronRight 
                                    className={`h-4 w-4 text-slate-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`} 
                                />
                            </button>
                            
                            {/* Section Content */}
                            {isExpanded && (
                                <div className="p-4 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                                    {sectionSteps.map(step => {
                                        const selectedValue = selections[step.step_id]?.[0] || '';
                                        
                                        return (
                                            <div key={step.step_id} className="space-y-1">
                                                <Label className="text-xs text-slate-600">
                                                    {step.label}
                                                    {step.is_required && <span className="text-red-500 ml-0.5">*</span>}
                                                </Label>
                                                <Select
                                                    value={selectedValue}
                                                    onValueChange={(val) => handleSelectionChange(step.step_id, val)}
                                                >
                                                    <SelectTrigger 
                                                        className="h-9"
                                                        data-testid={`expert-select-${step.step_id}`}
                                                    >
                                                        <SelectValue placeholder="Select..." />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        {step.options?.map(opt => (
                                                            <SelectItem key={opt.option_id} value={opt.option_id}>
                                                                {getOptionLabel(step, opt.option_id)}
                                                            </SelectItem>
                                                        ))}
                                                    </SelectContent>
                                                </Select>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    );
                })}

                {/* Price Estimate */}
                <div className="border-t pt-4 mt-6">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-slate-500">Estimated price</p>
                            {priceEstimate ? (
                                <>
                                    <p className="text-2xl font-bold text-emerald-600 font-display">
                                        €{priceEstimate.unit_price_estimate?.toFixed(2)}
                                        <span className="text-sm font-medium text-emerald-700 ml-1">/ unit</span>
                                    </p>
                                    <p className="text-xs text-slate-400 mt-0.5">
                                        €{priceEstimate.total_estimate?.toFixed(2)} total
                                    </p>
                                </>
                            ) : isCalculating ? (
                                <div className="flex items-center gap-2">
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                    <span className="text-sm text-slate-500">Calculating...</span>
                                </div>
                            ) : (
                                <p className="text-sm text-slate-400">Select options to get estimate</p>
                            )}
                        </div>
                        <Button 
                            onClick={() => onComplete && onComplete(buildFlowConfig(), priceEstimate)}
                            disabled={!priceEstimate}
                            data-testid="expert-confirm"
                        >
                            <Check className="h-4 w-4 mr-2" />
                            Confirm Configuration
                        </Button>
                    </div>
                </div>
            </CardContent>
        </Card>
    );
}
